import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../App";
import { darkTheme, lightTheme } from "../theme";
let t = darkTheme; // default — gets overridden by AdminDashboard state

// ─── Google Fonts ──────────────────────────────────────────────────────────────
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #0A0A0A; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #0A0A0A; }
    ::-webkit-scrollbar-thumb { background: #3D2E0A; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #C9A84C; }
  `}</style>
);

// ─── Nav ──────────────────────────────────────────────────────────────────────
const NAV = [
  { key: "portfolio",   label: "Portfolio"   },
  { key: "performance", label: "Performance" },
  { key: "signals",     label: "Signals"     },
  { key: "market",      label: "Market"      },
  { key: "scanner",     label: "Scanner"     },
  { key: "clients",     label: "Clients"     },
  { key: "pipeline",    label: "Pipeline"    },
  { key: "config",      label: "Config"      },
];

// ─── API Hook ─────────────────────────────────────────────────────────────────
function useAPI(endpoint, token) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const refetch = useCallback(() => {
    if (!endpoint || !token) return;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [endpoint, token]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
}

// ─── Shared Components ────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, mono }) {
  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderTop: `2px solid ${accent || t.purple}`,
      padding: "20px 24px",
      flex: 1, minWidth: 160,
    }}>
      <p style={{
        fontFamily: t.fontBody, fontSize: 10, fontWeight: 500,
        color: t.textMuted, letterSpacing: 3, textTransform: "uppercase",
        margin: "0 0 10px",
      }}>{label}</p>
      <p style={{
        fontFamily: t.fontDisplay,
        fontSize: 26,
        fontWeight: 400,
        color: t.textPrimary,
        margin: "0 0 4px",
      }}>{value}</p>
      {sub && <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, accent }) {
  return (
    <div style={{
      padding: "14px 20px",
      borderBottom: `1px solid ${t.border}`,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{ width: 3, height: 14, background: accent || t.purple, borderRadius: 2 }} />
      <span style={{
        fontFamily: t.fontBody, fontSize: 11, fontWeight: 500,
        color: t.textSilver, letterSpacing: 3, textTransform: "uppercase",
      }}>{title}</span>
    </div>
  );
}

function Loader() {
  return (
    <p style={{ fontFamily: t.fontBody, color: t.textMuted, textAlign: "center", padding: 48, fontSize: 12, letterSpacing: 2 }}>
      Loading...
    </p>
  );
}

function Empty({ message = "No data available" }) {
  return (
    <p style={{ fontFamily: t.fontBody, color: t.textMuted, textAlign: "center", padding: 48, fontSize: 12 }}>
      {message}
    </p>
  );
}

function DataTable({ data, columns, cols, rows }) {
  // Support both formats
  if (cols && rows) {
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: t.surfaceHigh }}>
              {cols.map((col, i) => (
                <th key={i} style={{
                  fontFamily: t.fontBody, color: t.textMuted, fontSize: 10,
                  fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase",
                  textAlign: "left", padding: "10px 14px",
                  borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
                }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceHigh}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {row.map((cell, j) => (
                  <td key={j} style={{
                    padding: "11px 14px", fontFamily: t.fontBody,
                    color: t.textSilver, fontSize: 13, whiteSpace: "nowrap",
                  }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data || data.length === 0) return <Empty />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: t.surfaceHigh }}>
            {columns.map(col => (
              <th key={col.key} style={{
                fontFamily: t.fontBody, color: t.textMuted, fontSize: 10,
                fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase",
                textAlign: "left", padding: "10px 14px",
                borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
              }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = t.surfaceHigh}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {columns.map(col => (
                <td key={col.key} style={{
                  padding: "11px 14px",
                  fontFamily: col.mono ? t.fontMono : t.fontBody,
                  color: t.textSilver, fontSize: col.mono ? 12 : 13,
                  whiteSpace: "nowrap",
                }}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionBadge({ action }) {
  const map = {
    "STRONG BUY":   { bg: "rgba(74,155,111,0.12)", color: "#4A9B6F", border: "rgba(74,155,111,0.3)"  },
    "BUY":          { bg: "rgba(74,155,111,0.07)", color: "#7EC8A0", border: "rgba(74,155,111,0.2)"  },
    "HOLD":         { bg: "rgba(107,107,136,0.1)", color: t.textMuted, border: t.border              },
    "SHORT":        { bg: "rgba(192,57,43,0.1)",   color: "#E07060", border: "rgba(192,57,43,0.3)"   },
    "STRONG SHORT": { bg: "rgba(139,26,26,0.15)",  color: "#E05050", border: "rgba(139,26,26,0.4)"   },
  };
  const c = map[action] || map["HOLD"];
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      padding: "3px 8px", fontSize: 10, fontFamily: t.fontBody,
      fontWeight: 500, letterSpacing: 1, borderRadius: 2,
    }}>{action}</span>
  );
}

// ─── Sector Bar ───────────────────────────────────────────────────────────────
function SectorBar({ positions }) {
  if (!positions?.length) return null;

 const sectorExposure = {};
positions.forEach(p => {
  if (p.side !== "LONG") return;
  const sector = p.sector || "Unknown";
  sectorExposure[sector] = (sectorExposure[sector] || 0) + (p.position_size_pct || 0);
});
const sectorData = Object.entries(sectorExposure)
  .map(([sector, pct]) => ({ sector, pct: pct * 100 }))
  .sort((a, b) => b.pct - a.pct);
const total = positions.reduce((s, p) => s + (p.dollar_amount || 0), 0);

  // Group by sector from ticker — rough mapping
  const SECTORS = {
    CLS: "Technology", TOST: "Financials", LTM: "Consumer", SNDK: "Technology",
    APP: "Technology", KEYS: "Technology", SMCI: "Technology", ADI: "Technology",
    TIGO: "Telecom", DELL: "Technology", AMX: "Telecom", IBM: "Technology",
    ALAB: "Technology", IONQ: "Technology", INSM: "Healthcare", SPOT: "Technology",
    GE: "Industrials", BNS: "Financials", SPG: "Real Estate", LLY: "Healthcare",
    HCA: "Healthcare", BMY: "Healthcare", RCI: "Telecom", BILI: "Technology", DB: "Financials",
  };

 positions.forEach(p => {
  if (p.side !== "LONG") return;
  const sector = p.sector || "Unknown";
  sectorExposure[sector] = (sectorExposure[sector] || 0) + (p.position_size_pct || 0);
});

  const SECTOR_COLORS = {
    Technology: t.purpleLight, Financials: "#4A9B8E", Healthcare: "#7EC8A0",
    Telecom: "#B8860B", Consumer: "#C9956C", Industrials: "#8888CC",
    "Real Estate": "#88AACC", Other: t.textMuted,
  };

 const sorted = Object.entries(sectorExposure).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", marginBottom: 16 }}>
      <p style={{ fontFamily: t.fontBody, fontSize: 10, fontWeight: 500, color: t.textMuted, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 16px" }}>
        Sector Exposure
      </p>
      {sorted.map(([sector, val]) => {
        const pct = (val * 100).toFixed(1);
        const color = SECTOR_COLORS[sector] || t.purple;
        return (
          <div key={sector} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11 }}>{sector}</span>
              <span style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 11 }}>{pct}%</span>
            </div>
            <div style={{ height: 4, background: t.border, borderRadius: 2 }}>
              <div style={{ height: 4, width: `${pct}%`, background: color, borderRadius: 2, transition: "width 0.5s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Signal Heatmap ───────────────────────────────────────────────────────────
function SignalHeatmap({ positions }) {
  if (!positions?.length) return null;
  const top10 = positions.slice(0, 10);

  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", marginBottom: 16 }}>
      <p style={{ fontFamily: t.fontBody, fontSize: 10, fontWeight: 500, color: t.textPrimary, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 16px" }}>
        Conviction Heatmap — Top 10
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
        {top10.map(p => {
          const conv = p.conviction_score || 0;
          const intensity = Math.min(conv * 2, 1);
          const bg = p.side === "SHORT"
            ? `rgba(139,26,26,${intensity * 0.8})`
            : `rgba(123,94,167,${intensity * 0.8})`;
          return (
            <div key={p.ticker} style={{
              background: bg,
              border: `1px solid ${p.side === "SHORT" ? t.ruby : t.purpleDim}`,
              padding: "10px 8px", textAlign: "center",
              borderRadius: 2,
            }}>
               <p style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 12, margin: "0 0 4px", fontWeight: 400 }}>{p.ticker}</p>
              <p style={{ fontFamily: t.fontMono, color: "#FFFFFF", fontSize: 11, margin: 0, textShadow: "0 1px 3px rgba(0,0,0,0.8)", fontWeight: 500 }}>{(conv * 100).toFixed(0)}%</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Risk Gauges ──────────────────────────────────────────────────────────────
function RiskGauges({ positions, regime, macro }) {
  const totalDeployed = positions?.filter(p => p.side === "LONG").reduce((s, p) => s + (p.dollar_amount || 0), 0) || 0;
  const deployedPct   = totalDeployed / 1000000;
  const kellyBudget   = positions?.[0]?.kelly_budget || 0;
  const cvar          = -0.28; // from risk.py output
  const vix           = macro?.vix || 0;

  const Gauge = ({ label, value, max, color, format }) => {
    const pct = Math.min((value / max) * 100, 100);
    return (
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontFamily: t.fontBody, fontSize: 10, color: t.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>{label}</span>
          <span style={{ fontFamily: t.fontMono, fontSize: 12, color }}>{format ? format(value) : value.toFixed(2)}</span>
        </div>
        <div style={{ height: 6, background: t.border, borderRadius: 3 }}>
          <div style={{ height: 6, width: `${pct}%`, background: color, borderRadius: 3, transition: "width 0.5s ease" }} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", marginBottom: 16 }}>
      <p style={{ fontFamily: t.fontBody, fontSize: 10, fontWeight: 500, color: t.textMuted, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 16px" }}>
        Risk Utilization
      </p>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <Gauge label="Deployed" value={deployedPct} max={1}    color={t.purple}   format={v => `${(v*100).toFixed(1)}%`} />
        <Gauge label="Kelly Budget" value={kellyBudget} max={1} color={t.purpleLight} format={v => `${(v*100).toFixed(1)}%`} />
        <Gauge label="VIX"      value={vix}          max={50}  color={vix > 30 ? t.rubyLight : "#B8860B"} format={v => v.toFixed(1)} />
        <Gauge label="CVaR 95%" value={Math.abs(cvar)} max={5}  color={t.rubyLight} format={v => `-${v.toFixed(2)}%`} />
      </div>
    </div>
  );
}

// ─── Market Status Strip ──────────────────────────────────────────────────────
function MarketStrip({ regime, macro, topology }) {
  const regimeColor = { bull: t.positive, sideways: "#B8860B", bear: t.rubyLight };
  const stressColor = { low: t.positive, medium: "#B8860B", high: t.rubyLight };

  const items = [
    { label: "Regime",   value: regime?.regime?.toUpperCase() || "—",             color: regimeColor[regime?.regime] || t.textMuted },
    { label: "VIX",      value: macro?.vix?.toFixed(1) || "—",                    color: (macro?.vix || 0) > 30 ? t.rubyLight : t.textSilver },
    { label: "Macro",    value: macro?.macro_score?.toFixed(3) || "—",            color: (macro?.macro_score || 0) > 0 ? t.positive : t.rubyLight },
    { label: "Topology", value: topology?.stress_level?.toUpperCase() || "—",     color: stressColor[topology?.stress_level] || t.textMuted },
    { label: "Conf",     value: regime?.regime_probability ? `${(regime.regime_probability*100).toFixed(0)}%` : "—", color: t.textSilver },
  ];

  return (
    <div style={{
      background: t.surfaceHigh,
      border: `1px solid ${t.border}`,
      borderLeft: `3px solid ${t.purple}`,
      display: "flex", gap: 0, marginBottom: 24, flexWrap: "wrap",
      width: "100%",
    }}>
      {items.map((item, i) => (
        <div key={item.label} style={{
          padding: "16px 24px 12px 24px",
          borderRight: `1px solid ${t.border}`,
          display: "flex", flexDirection: "column", gap: 4,
          flex: 1, alignItems: "center", overflow: "visible",
        }}>
          <span style={{ fontFamily: t.fontBody, fontSize: 9, color: t.textMuted, letterSpacing: 3, textTransform: "uppercase" }}>{item.label}</span>
          <span style={{ fontFamily: t.fontMono, fontSize: 14, color: item.color, fontWeight: 400 }}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Portfolio Tab ─────────────────────────────────────────────────────────────
function PortfolioTab({ token }) {
  const { data: positions, loading: pLoad } = useAPI("/api/investing/positions", token);
  const { data: exits,     loading: eLoad } = useAPI("/api/investing/exits",     token);
  const { data: perf                      } = useAPI("/api/investing/performance", token);
  const { data: regime                    } = useAPI("/api/market/regime",   token);
  const { data: macro                     } = useAPI("/api/market/macro",    token);
  const { data: topology                  } = useAPI("/api/market/topology", token);
  const { data: trades                    } = useAPI("/api/investing/trades", token);
  const { data: monitorData } = useAPI("/api/monitor/status", token);

  const longs  = positions?.filter(p => p.side === "LONG")  || [];
  const shorts = positions?.filter(p => p.side === "SHORT") || [];
  const totalDeployed = longs.reduce((s, p) => s + (p.dollar_amount || 0), 0);
  const avgConviction = longs.length
    ? longs.reduce((s, p) => s + (p.conviction_score || 0), 0) / longs.length
    : 0;

  const longCols = [
    { key: "ticker",            label: "Ticker",     render: v => <span style={{ color: t.purpleLight, fontFamily: t.fontMono }}>{v}</span> },
    { key: "action",            label: "Signal",     render: v => <ActionBadge action={v} /> },
    { key: "conviction_score",  label: "Conviction", render: v => `${((v||0)*100).toFixed(1)}%`, mono: true },
    { key: "signals_agreeing",  label: "Signals",    mono: true },
    { key: "position_size_pct", label: "Size",       render: v => `${((v||0)*100).toFixed(2)}%`, mono: true },
    { key: "dollar_amount",     label: "Value",      render: v => `$${(v||0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, mono: true },
    { key: "current_price",     label: "Price",      render: v => v ? `$${Number(v).toLocaleString()}` : "—", mono: true },
    { key: "shares",            label: "Shares",     mono: true },
    { key: "vol_scalar",        label: "Vol ×",      render: v => v?.toFixed(2) || "—", mono: true },
    { key: "momentum_scalar",   label: "Mom ×",      render: v => v?.toFixed(2) || "—", mono: true },
  ];

  const shortCols = [
    { key: "ticker",            label: "Ticker",     render: v => <span style={{ color: t.rubyLight, fontFamily: t.fontMono }}>{v}</span> },
    { key: "conviction_score",  label: "Conviction", render: v => `${((v||0)*100).toFixed(1)}%`, mono: true },
    { key: "signals_agreeing",  label: "Signals",    mono: true },
    { key: "position_size_pct", label: "Size",       render: v => `${((v||0)*100).toFixed(2)}%`, mono: true },
    { key: "dollar_amount",     label: "Value",      render: v => `$${(v||0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, mono: true },
    { key: "current_price",     label: "Price",      render: v => v ? `$${v}` : "—", mono: true },
    { key: "shares",            label: "Shares",     mono: true },
  ];

  const exitCols = [
    { key: "ticker",        label: "Ticker",   render: v => <span style={{ color: t.purpleLight, fontFamily: t.fontMono }}>{v}</span> },
    { key: "exit_decision", label: "Decision", render: v => (
      <span style={{ color: v?.includes("CLOSE") ? t.rubyLight : v?.includes("REDUCE") ? "#B8860B" : t.positive, fontSize: 12 }}>{v}</span>
    )},
    { key: "exit_reason",   label: "Reason",   render: v => <span style={{ color: t.textMuted, fontSize: 11 }}>{v}</span> },
  ];

  const recentTrades = trades?.slice(-8).reverse() || [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, margin: 0 }}>
          Portfolio Command Center
        </h2>
        <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11 }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </span>
      </div>

      {/* Market status strip */}
      <MarketStrip regime={regime} macro={macro} topology={topology} />

      {/* Top stat cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <StatCard label="Long Positions"   value={longs.length}  sub="Active longs" />
        <StatCard label="Short Positions"  value={shorts.length} sub="Active shorts" accent={t.ruby} />
        <StatCard label="Deployed Capital" value={`$${totalDeployed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} mono />
        <StatCard label="Avg Conviction"   value={`${(avgConviction * 100).toFixed(1)}%`} mono />
        {perf && <StatCard label="Win Rate"  value={`${((perf.win_rate||0)*100).toFixed(1)}%`} sub={`${perf.total_trades} trades`} mono />}
        {perf && <StatCard label="Total P&L" value={`$${(perf.total_pnl||0).toLocaleString()}`} accent={perf.total_pnl >= 0 ? t.positive : t.ruby} mono />}
      </div>

      {/* Two column layout — risk gauges + sector bar */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 0 }}>
        <RiskGauges positions={positions} regime={regime} macro={macro} />
        <SectorBar  positions={longs} />
      </div>

      {/* Heatmap + recent trades */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <SignalHeatmap positions={longs} />

        {/* Recent trades */}
        <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
          <SectionHeader title="Recent Trades" />
          {recentTrades.length === 0 ? (
            <Empty message="No trades yet" />
          ) : (
            recentTrades.map((trade, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 16px", borderBottom: `1px solid ${t.border}`,
              }}>
                <span style={{ fontFamily: t.fontMono, color: t.purpleLight, fontSize: 12 }}>{trade.ticker}</span>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11 }}>{trade.action}</span>
                <span style={{ fontFamily: t.fontMono, color: trade.pnl >= 0 ? t.positive : t.rubyLight, fontSize: 12 }}>
                  {trade.pnl != null ? `$${trade.pnl.toLocaleString()}` : "—"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Positions tables */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, marginBottom: 16 }}>
        <SectionHeader title={`Long Positions (${longs.length})`} />
        {pLoad ? <Loader /> : <DataTable data={longs} columns={longCols} />}
      </div>

      {shorts.length > 0 && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, marginBottom: 16 }}>
          <SectionHeader title={`Short Positions (${shorts.length})`} accent={t.ruby} />
          {pLoad ? <Loader /> : <DataTable data={shorts} columns={shortCols} />}
        </div>
      )}

      {exits && exits.length > 0 && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
          <SectionHeader title="Exit Decisions" accent={t.ruby} />
          {eLoad ? <Loader /> : <DataTable data={exits} columns={exitCols} />}
        </div>
      )}
    </div>
  );
}

// ─── Signals Tab ──────────────────────────────────────────────────────────────
function SignalsTab({ token }) {
  const [active, setActive] = useState("ensemble");

  const LAYERS = {
    ensemble:       { endpoint: "/api/signals/ensemble",       label: "Ensemble"       },
    tcn:            { endpoint: "/api/signals/tcn",            label: "TCN"            },
    xgboost:        { endpoint: "/api/signals/xgboost",        label: "XGBoost"        },
    options:        { endpoint: "/api/signals/options",        label: "Options Flow"   },
    insider:        { endpoint: "/api/signals/insider",        label: "Insider"        },
    short_interest: { endpoint: "/api/signals/short_interest", label: "Short Interest" },
    sentiment:      { endpoint: "/api/signals/sentiment",      label: "Sentiment"      },
    earnings:       { endpoint: "/api/signals/earnings",       label: "Earnings"       },
    cross_asset:    { endpoint: "/api/signals/cross_asset",    label: "Cross Asset"    },
    macro:          { endpoint: "/api/signals/macro",          label: "Macro"          },
    stat_arb:       { endpoint: "/api/signals/stat_arb",       label: "Stat Arb"       },
    graph:          { endpoint: "/api/signals/graph",          label: "Graph"          },
    momentum: { endpoint: "/api/signals/momentum", label: "Momentum" },
  };

  const { data, loading } = useAPI(LAYERS[active].endpoint, token);
  // key forces remount on tab change

  // Dynamically build columns from data
  const columns = data?.[0]
    ? Object.keys(data[0]).slice(0, 10).map(k => ({
        key: k, label: k,
        mono: typeof data[0][k] === "number",
        render: typeof data[0][k] === "number"
          ? v => (v !== null && v !== undefined) ? Number(v).toFixed(4) : "—"
          : undefined,
      }))
    : [];

  return (
    <div>
      <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, marginBottom: 28 }}>
        Signal Layers
      </h2>

      {/* Layer selector */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {Object.entries(LAYERS).map(([key, val]) => (
          <button key={key} onClick={() => setActive(key)} style={{
            background: active === key ? t.purple : "transparent",
            color: active === key ? t.textPrimary : t.textMuted,
            border: `1px solid ${active === key ? t.purple : t.border}`,
            padding: "7px 14px", fontSize: 11, fontFamily: t.fontBody,
            fontWeight: 500, letterSpacing: 1, textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.15s",
            boxShadow: active === key ? `0 0 12px ${t.purpleGlow}` : "none",
          }}>{val.label}</button>
        ))}
      </div>

      <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
        <SectionHeader title={`${LAYERS[active].label} — ${data?.length || 0} records`} />
        {loading ? <Loader /> : <DataTable data={data || []} columns={columns} />}
      </div>
    </div>
  );
}

// ─── Market Tab ───────────────────────────────────────────────────────────────
function MarketTab({ token }) {
  const { data: regime,   loading: rLoad } = useAPI("/api/market/regime",   token);
  const { data: macro,    loading: mLoad } = useAPI("/api/market/macro",    token);
  const { data: topology, loading: tLoad } = useAPI("/api/market/topology", token);

  const regimeColor = { bull: t.positive, sideways: "#B8860B", bear: t.rubyLight };
  const stressColor = { low: t.positive, medium: "#B8860B", high: t.rubyLight };

  return (
    <div>
      <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, marginBottom: 28 }}>
        Market Overview
      </h2>

      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard
          label="Regime"
          value={regime?.regime?.toUpperCase() || "—"}
          sub={regime?.regime_probability ? `${(regime.regime_probability * 100).toFixed(1)}% confidence` : ""}
          accent={regimeColor[regime?.regime] || t.purple}
        />
        <StatCard label="VIX"          value={macro?.vix?.toFixed(2) || "—"}           mono />
        <StatCard label="Macro Score"  value={macro?.macro_score?.toFixed(4) || "—"}   mono />
        <StatCard
          label="Topology Stress"
          value={topology?.stress_level?.toUpperCase() || "—"}
          accent={stressColor[topology?.stress_level] || t.purple}
        />
        <StatCard label="Topology Score" value={topology?.stress_score?.toFixed(4) || "—"} mono />
      </div>

      {/* Macro detail */}
      {macro && !mLoad && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, marginBottom: 16 }}>
          <SectionHeader title="Macro Factors" />
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 0,
          }}>
            {Object.entries(macro).map(([k, v]) => (
              <div key={k} style={{
                padding: "14px 20px",
                borderBottom: `1px solid ${t.border}`,
                borderRight: `1px solid ${t.border}`,
              }}>
                <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 6px" }}>{k}</p>
                <p style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 14, margin: 0 }}>
                  {typeof v === "number" ? v.toFixed(4) : String(v ?? "—")}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regime detail */}
      {regime && !rLoad && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
          <SectionHeader title="Regime Detail" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 0 }}>
            {Object.entries(regime).map(([k, v]) => (
              <div key={k} style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border}`, borderRight: `1px solid ${t.border}` }}>
                <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 6px" }}>{k}</p>
                <p style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 14, margin: 0 }}>
                  {typeof v === "number" ? v.toFixed(4) : String(v ?? "—")}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ScanPanel = ({ ticker, setTicker, result, loading, onScan, label, activeLayer, renderChart }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Search bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          value={ticker}
          onChange={e => setTicker(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === "Enter" && onScan()}
          placeholder={label}
          style={{ flex: 1, background: t.surface, border: `1px solid ${t.border}`, borderBottom: `1px solid ${t.purple}`, color: t.textPrimary, padding: "10px 14px", fontSize: 14, fontFamily: t.fontMono, outline: "none" }}
        />
        <button onClick={onScan} disabled={loading} style={{ background: loading ? t.purpleDim : t.purple, color: t.textPrimary, border: "none", padding: "10px 20px", fontSize: 10, fontFamily: t.fontBody, fontWeight: 500, letterSpacing: 3, textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "..." : "SCAN"}
        </button>
      </div>

      {/* Summary bar */}
      {result && !result.error && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "6px 14px" }}>
            <span style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 22 }}>{result.ticker || ticker}</span>
          </div>
          {result.ensemble && (
            <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "6px 14px", display: "flex", alignItems: "center" }}>
              <ActionBadge action={result.ensemble.action} />
            </div>
          )}
          {result.risk && (
            <>
              <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "6px 14px" }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, display: "block", textTransform: "uppercase" }}>Conviction</span>
                <span style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 14 }}>{((result.risk.conviction_score || 0) * 100).toFixed(1)}%</span>
              </div>
              <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "6px 14px" }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, display: "block", textTransform: "uppercase" }}>Signals</span>
                <span style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 14 }}>{result.risk.signals_agreeing ?? "—"} / 13</span>
              </div>
              <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "6px 14px" }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, display: "block", textTransform: "uppercase" }}>Size</span>
                <span style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 14 }}>{result.risk.position_size_pct ? `${(result.risk.position_size_pct * 100).toFixed(2)}%` : "—"}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Chart area */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", minHeight: 320 }}>
        {loading ? <Loader /> : renderChart(result, activeLayer)}
      </div>
    </div>
  );

// ─── Scanner Tab ──────────────────────────────────────────────────────────────
function ScannerTab({ token }) {
  const [tickerA, setTickerA] = useState("");
  const [tickerB, setTickerB] = useState("");
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [activeLayer, setActiveLayer] = useState("prediction");
  const [compareMode, setCompareMode] = useState(false);

  const scan = async (ticker, setResult, setLoading) => {
    if (!ticker.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/scanner/${ticker.trim().toUpperCase()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Ticker not found");
      setResult(await res.json());
    } catch (e) {
      setResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const LAYERS = [
    { id: "prediction", label: "Prediction", color: "#7B5EA7" },
    { id: "ensemble",   label: "Ensemble",   color: "#A78BDB" },
    { id: "tcn",        label: "TCN",        color: "#5BA4CF" },
    { id: "xgboost",    label: "XGBoost",    color: "#4A9B6F" },
    { id: "options",    label: "Options",    color: "#E8A838" },
    { id: "insider",    label: "Insider",    color: "#A78BDB" },
    { id: "short_interest", label: "Short Interest", color: "#C0392B" },
    { id: "sentiment",  label: "Sentiment",  color: "#5BA4CF" },
    { id: "cross_asset",label: "Cross Asset",color: "#4A9B6F" },
    { id: "earnings",   label: "Earnings",   color: "#E8A838" },
    { id: "graph",      label: "Graph",      color: "#7B5EA7" },
    { id: "macro",      label: "Macro",      color: "#5BA4CF" },
    { id: "regime",     label: "Regime",     color: "#4A9B6F" },
    { id: "momentum",  label: "Momentum",  color: "#A78BDB" },
    { id: "topology",   label: "Topology",   color: "#C0392B" },
  ];

  // ── Chart renderers ────────────────────────────────────────────────────────

  const PredictionChart = ({ result }) => {
    if (!result || result.error) return <Empty message="No data" />;
    const risk = result.risk || {};
    const ens = result.ensemble || {};
    const score = ens.ensemble_score || 0;
    const conviction = risk.conviction_score || 0;
    const price = risk.current_price && risk.current_price > 0 ? risk.current_price : (result.price || result.current_price || 50);
    const action = ens.action || "HOLD";
    const isShort = risk.side === "SHORT";

    // Generate synthetic price projection based on ensemble score
    const months = 12;
    const points = months * 4; // weekly points
    const baseReturn = score * (isShort ? -1 : 1);
    const annualReturn = baseReturn * 8; // scale ensemble score to annual return
    const weeklyReturn = annualReturn / 52;
    const volatility = (risk.vol_scalar || 0.3) * 0.02;

    const projectionData = [];
    let currentPrice = price;
    for (let i = 0; i <= points; i++) {
      const week = i;
      const trend = currentPrice * (1 + weeklyReturn);
      const upper = trend * (1 + volatility * Math.sqrt(i + 1) * 0.5);
      const lower = trend * (1 - volatility * Math.sqrt(i + 1) * 0.5);
      projectionData.push({ week, price: trend, upper, lower });
      currentPrice = trend;
    }

    const maxPrice = Math.max(...projectionData.map(d => d.upper));
    const minPrice = Math.min(...projectionData.map(d => d.lower));
    const priceRange = maxPrice - minPrice;
    const W = 800, H = 320;
    const pad = { top: 20, right: 40, bottom: 40, left: 70 };
    const chartW = W - pad.left - pad.right;
    const chartH = H - pad.top - pad.bottom;

    const xScale = (i) => pad.left + (i / points) * chartW;
    const yScale = (p) => pad.top + chartH - ((p - minPrice) / priceRange) * chartH;

    const trendPath = projectionData.map((d, i) =>
      `${i === 0 ? "M" : "L"} ${xScale(d.week)} ${yScale(d.price)}`
    ).join(" ");

    const upperPath = projectionData.map((d, i) =>
      `${i === 0 ? "M" : "L"} ${xScale(d.week)} ${yScale(d.upper)}`
    ).join(" ");

    const lowerPath = projectionData.map((d, i) =>
      `${i === 0 ? "M" : "L"} ${xScale(d.week)} ${yScale(d.lower)}`
    ).join(" ");

    const bandPath = upperPath + " " +
      [...projectionData].reverse().map((d, i) =>
        `${i === 0 ? "L" : "L"} ${xScale(d.week)} ${yScale(d.lower)}`
      ).join(" ") + " Z";

    const lineColor = "#9B59B6";
    const yTicks = 5;

    return (
      <div>
        <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            ["Entry Price", price ? `$${price.toFixed(2)}` : "—"],
            ["12M Target", price ? `$${(price * (1 + annualReturn)).toFixed(2)}` : "—"],
            ["Expected Return", `${(annualReturn * 100).toFixed(1)}%`],
            ["Conviction", `${(conviction * 100).toFixed(1)}%`],
            ["Signals", risk.signals_agreeing ?? "—"],
          ].map(([label, val]) => (
            <div key={label} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "10px 16px", flex: 1, minWidth: 100 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{label}</p>
              <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 20, margin: 0 }}>{val}</p>
            </div>
          ))}
        </div>

        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
          {/* Grid lines */}
          {Array.from({ length: yTicks }).map((_, i) => {
            const y = pad.top + (i / (yTicks - 1)) * chartH;
            const price = maxPrice - (i / (yTicks - 1)) * priceRange;
            return (
              <g key={i}>
                <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke={t.border} strokeWidth="1" />
                <text x={pad.left - 8} y={y + 4} textAnchor="end" fill={t.textMuted} fontSize="10" fontFamily="monospace">${price.toFixed(0)}</text>
              </g>
            );
          })}
          {/* Month labels */}
          {[0, 2, 4, 6, 8, 10, 12].map(month => {
            const week = month * 4;
            return (
              <text key={month} x={xScale(week)} y={H - 10} textAnchor="middle" fill={t.textMuted} fontSize="10" fontFamily="monospace">
                {month === 0 ? "Now" : `M${month}`}
              </text>
            );
          })}
          {/* Green band: above trend line */}
          <path d={upperPath + " " + [...projectionData].reverse().map((d,i) => `L ${xScale(d.week)} ${yScale(d.price)}`).join(" ") + " Z"} fill="rgba(74,155,111,0.15)" />
          {/* Red band: below trend line */}
          <path d={trendPath + " " + [...projectionData].reverse().map((d,i) => `L ${xScale(d.week)} ${yScale(d.lower)}`).join(" ") + " Z"} fill="rgba(232,93,74,0.15)" />
          <path d={upperPath} stroke="#4A9B6F" strokeWidth="1" strokeDasharray="4,4" fill="none" strokeOpacity="0.6" />
          <path d={lowerPath} stroke="#E85D4A" strokeWidth="1" strokeDasharray="4,4" fill="none" strokeOpacity="0.6" />
          {/* Trend line */}
          <path d={trendPath} stroke="#9B59B6" strokeWidth="2.5" fill="none" />
          {/* Current price dot */}
          <circle cx={xScale(0)} cy={yScale(price)} r="5" fill={lineColor} />
          <text x={xScale(0) + 10} y={yScale(price) + 4} fill={t.textPrimary} fontSize="11" fontFamily="monospace">${price.toFixed(2)}</text>
          {/* Action label */}
          <rect x={W - pad.right - 100} y={pad.top} width={90} height={24} fill={lineColor} fillOpacity="0.2" stroke={lineColor} strokeWidth="1" />
          <text x={W - pad.right - 55} y={pad.top + 16} textAnchor="middle" fill={lineColor} fontSize="11" fontFamily="monospace" fontWeight="bold">{action}</text>
        </svg>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, margin: "8px 0 0", fontStyle: "italic" }}>
          * Projection based on ensemble score × conviction. Confidence bands widen with time horizon uncertainty.
        </p>
      </div>
    );
  };

  const EnsembleChart = ({ result }) => {
    if (!result?.ensemble) return <Empty message="No ensemble data" />;
    const ens = result.ensemble;
    const layers = [
      { label: "Technical",     val: ens.technical || 0,      color: "#5BA4CF" },
      { label: "TCN",           val: ens.tcn || 0,            color: "#A78BDB" },
      { label: "XGBoost",       val: ens.xgboost || 0,        color: "#4A9B6F" },
      { label: "Options",       val: ens.options || 0,        color: "#E8A838" },
      { label: "Insider",       val: ens.insider || 0,        color: "#A78BDB" },
      { label: "Short Interest",val: ens.short_interest || 0, color: "#C0392B" },
      { label: "Cross Asset",   val: ens.cross_asset || 0,    color: "#4A9B6F" },
      { label: "Momentum",     val: ens.momentum || 0,       color: "#A78BDB" },
    ];
    const maxVal = Math.max(...layers.map(l => Math.abs(l.val)), 0.1);

    return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 4px" }}>Ensemble Score</p>
        <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 36, margin: 0 }}>{(ens.ensemble_score || 0).toFixed(4)}</p>
      </div>
      {layers.map(({ label, val, color }) => {
        const pct = (Math.abs(val) / maxVal) * 100;
        const isNeg = val < 0;
        return (
          <div key={label} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>{label}</span>
              <span style={{ fontFamily: t.fontMono, color: val === 0 ? t.textMuted : isNeg ? "#C0392B" : t.textSilver, fontSize: 12 }}>{val > 0 ? "+" : ""}{val.toFixed(4)}</span>
            </div>
            <div style={{ height: 6, background: t.border, borderRadius: 3 }}>
              <div style={{ height: 6, width: val === 0 ? "2px" : `${pct}%`, background: val === 0 ? t.textMuted : isNeg ? "#C0392B" : color, borderRadius: 3, transition: "width 0.6s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

  const TCNChart = ({ result }) => {
    if (!result?.tcn) return <Empty message="No TCN data" />;
    const tcn = result.tcn;
    const probs = [
      { label: "Prob Down",    val: tcn.tcn_prob_down || 0,    color: "#C0392B" },
      { label: "Prob Neutral", val: tcn.tcn_prob_neutral || 0, color: "#6B6B88" },
      { label: "Prob Up",      val: tcn.tcn_prob_up || 0,      color: "#4A9B6F" },
    ];
    const W = 300, H = 300, cx = 150, cy = 150, r = 110;

    let startAngle = -Math.PI / 2;
    const slices = probs.map(p => {
      const angle = p.val * 2 * Math.PI;
      const endAngle = startAngle + angle;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);
      const largeArc = angle > Math.PI ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      const midAngle = startAngle + angle / 2;
      const lx = cx + (r * 0.65) * Math.cos(midAngle);
      const ly = cy + (r * 0.65) * Math.sin(midAngle);
      startAngle = endAngle;
      return { ...p, path, lx, ly };
    });

    return (
      <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          {slices.map(s => (
            <g key={s.label}>
              <path d={s.path} fill={s.color} fillOpacity="0.85" stroke={t.bg} strokeWidth="2" />
              {s.val > 0.05 && (
                <text x={s.lx} y={s.ly} textAnchor="middle" fill="white" fontSize="13" fontFamily="monospace" fontWeight="bold">
                  {(s.val * 100).toFixed(0)}%
                </text>
              )}
            </g>
          ))}
          <circle cx={cx} cy={cy} r={45} fill={t.bg} />
          <text x={cx} y={cy - 8} textAnchor="middle" fill={t.textPrimary} fontSize="14" fontFamily="monospace">{tcn.tcn_signal === 1 ? "BUY" : tcn.tcn_signal === -1 ? "SELL" : "HOLD"}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fill={t.textMuted} fontSize="11" fontFamily="monospace">{((tcn.tcn_confidence || 0) * 100).toFixed(0)}% conf</text>
        </svg>
        <div>
          {probs.map(p => (
            <div key={p.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 12, height: 12, background: p.color, borderRadius: 2 }} />
              <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 13, width: 110 }}>{p.label}</span>
              <span style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 16 }}>{(p.val * 100).toFixed(1)}%</span>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: "12px 16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
            <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, margin: "0 0 4px", textTransform: "uppercase" }}>Confidence</p>
            <p style={{ fontFamily: t.fontDisplay, color: "#5BA4CF", fontSize: 28, margin: 0 }}>{((tcn.tcn_confidence || 0) * 100).toFixed(1)}%</p>
          </div>
        </div>
      </div>
    );
  };

  const XGBoostChart = ({ result }) => {
    if (!result?.xgboost) return <Empty message="No XGBoost data" />;
    const xgb = result.xgboost;
    const probs = [
      { label: "Down",    val: xgb.xgb_prob_down || 0,    color: "#C0392B" },
      { label: "Neutral", val: xgb.xgb_prob_neutral || 0, color: "#6B6B88" },
      { label: "Up",      val: xgb.xgb_prob_up || 0,      color: "#4A9B6F" },
    ];
    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {[
            ["Signal",     xgb.xgb_signal === 1 ? "BUY" : xgb.xgb_signal === -1 ? "SELL" : "HOLD"],
            ["Confidence", `${((xgb.xgb_confidence || 0) * 100).toFixed(1)}%`],
          ].map(([l, v]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 20px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 28, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 12px" }}>Probability Distribution</p>
        {probs.map(({ label, val, color }) => (
          <div key={label} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 13 }}>{label}</span>
              <span style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 13 }}>{(val * 100).toFixed(1)}%</span>
            </div>
            <div style={{ height: 10, background: t.border, borderRadius: 5 }}>
              <div style={{ height: 10, width: `${val * 100}%`, background: color, borderRadius: 5, transition: "width 0.6s ease" }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const OptionsChart = ({ result }) => {
    if (!result?.options) return <Empty message="No options data" />;
    const opt = result.options;
    const pcr = opt.put_call_ratio || 0;
    const isBullish = pcr < 0.7;
    const pcrColor = pcr < 0.5 ? "#4A9B6F" : pcr < 1.0 ? "#E8A838" : "#C0392B";
    const gaugePct = Math.min(pcr / 3, 1) * 100;

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["Options Score",  (opt.options_score || 0).toFixed(2)],
            ["Put/Call Ratio", pcr.toFixed(3)],
            ["Call Volume",    (opt.total_call_volume || 0).toLocaleString()],
            ["Put Volume",     (opt.total_put_volume || 0).toLocaleString()],
          ].map(([l, v]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px", flex: 1, minWidth: 120 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 22, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        {/* P/C Ratio gauge */}
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 8px" }}>Put/Call Ratio Gauge</p>
        <div style={{ position: "relative", height: 24, background: `linear-gradient(to right, #4A9B6F, #E8A838, #C0392B)`, borderRadius: 12, marginBottom: 6 }}>
          <div style={{ position: "absolute", left: `${gaugePct}%`, top: -4, transform: "translateX(-50%)", width: 16, height: 32, background: "white", borderRadius: 4, border: `2px solid ${t.bg}` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontFamily: t.fontMono, color: "#4A9B6F", fontSize: 10 }}>Bullish (0)</span>
          <span style={{ fontFamily: t.fontMono, color: pcrColor, fontSize: 12, fontWeight: "bold" }}>PCR: {pcr.toFixed(2)}</span>
          <span style={{ fontFamily: t.fontMono, color: "#C0392B", fontSize: 10 }}>Bearish (3+)</span>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          {[
            ["Unusual Calls", opt.unusual_call_activity ? "YES" : "NO", opt.unusual_call_activity ? "#4A9B6F" : t.textMuted],
            ["Unusual Puts",  opt.unusual_put_activity  ? "YES" : "NO", opt.unusual_put_activity  ? "#C0392B" : t.textMuted],
            ["Top Call Strike", opt.top_call_strike ? `$${opt.top_call_strike}` : "—", t.textSilver],
            ["Top Put Strike",  opt.top_put_strike  ? `$${opt.top_put_strike}`  : "—", t.textSilver],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "10px 14px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontMono, color: c, fontSize: 14, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const InsiderChart = ({ result }) => {
    if (!result?.insider) return <Empty message="No insider data" />;
    const ins = result.insider;
    const score = ins.insider_score || 0;
    const filings = ins.filing_count || 0;
    const scoreColor = score >= 0.7 ? "#4A9B6F" : score >= 0.4 ? "#E8A838" : t.textMuted;
    const bars = Math.min(filings, 20);

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {[
            ["Insider Score", score.toFixed(2), scoreColor],
            ["Total Filings", filings, t.textPrimary],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "16px 24px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 36, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 12px" }}>Filing Activity (relative)</p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80 }}>
          {Array.from({ length: bars }).map((_, i) => {
            const h = Math.max(20, Math.random() * 80 * (score + 0.1));
            return (
              <div key={i} style={{ flex: 1, height: `${h}%`, background: scoreColor, opacity: 0.6 + (i / bars) * 0.4, borderRadius: "2px 2px 0 0" }} />
            );
          })}
        </div>

        <div style={{ marginTop: 20, padding: "12px 16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{ins.signal || "No signal available"}</p>
        </div>
      </div>
    );
  };

  const ShortInterestChart = ({ result }) => {
    if (!result?.short_interest) return <Empty message="No short interest data" />;
    const si = result.short_interest;
    const shortPct = (si.short_pct_float || 0) * 100;
    const changeM = (si.short_change_mom || 0) * 100;
    const isRising = changeM > 0;

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["Short % Float",    `${shortPct.toFixed(1)}%`, shortPct > 20 ? "#C0392B" : shortPct > 10 ? "#E8A838" : "#4A9B6F"],
            ["Short Ratio",      (si.short_ratio || 0).toFixed(1) + "x", t.textPrimary],
            ["MoM Change",       `${changeM > 0 ? "+" : ""}${changeM.toFixed(1)}%`, isRising ? "#C0392B" : "#4A9B6F"],
            ["Squeeze Potential",si.squeeze_potential ? "YES" : "NO", si.squeeze_potential ? "#E8A838" : t.textMuted],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px", flex: 1, minWidth: 110 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 22, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 8px" }}>Short Interest Level</p>
        <div style={{ height: 16, background: `linear-gradient(to right, #4A9B6F, #E8A838, #C0392B)`, borderRadius: 8, marginBottom: 6, position: "relative" }}>
          <div style={{ position: "absolute", left: `${Math.min(shortPct / 40 * 100, 100)}%`, top: -4, transform: "translateX(-50%)", width: 14, height: 24, background: "white", borderRadius: 4, border: `2px solid ${t.bg}` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontFamily: t.fontMono, color: "#4A9B6F", fontSize: 10 }}>Low (0%)</span>
          <span style={{ fontFamily: t.fontMono, color: "#E8A838", fontSize: 10 }}>High (20%)</span>
          <span style={{ fontFamily: t.fontMono, color: "#C0392B", fontSize: 10 }}>Extreme (40%+)</span>
        </div>

        <div style={{ padding: "12px 16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{si.signal || "—"}</p>
        </div>
      </div>
    );
  };

  const SentimentChart = ({ result }) => {
    if (!result?.sentiment) return <Empty message="No sentiment data" />;
    const sent = result.sentiment;
    const score = sent.sentiment_score || 0;
    const pct = ((score + 1) / 2) * 100;
    const sentColor = score > 0.2 ? "#4A9B6F" : score < -0.2 ? "#C0392B" : "#E8A838";

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {[
            ["Sentiment Score", score.toFixed(4), sentColor],
            ["Headlines Analyzed", sent.headline_count || 0, t.textPrimary],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "16px 24px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 36, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 8px" }}>Sentiment Spectrum</p>
        <div style={{ height: 16, background: `linear-gradient(to right, #C0392B, #6B6B88, #4A9B6F)`, borderRadius: 8, marginBottom: 6, position: "relative" }}>
          <div style={{ position: "absolute", left: `${pct}%`, top: -4, transform: "translateX(-50%)", width: 14, height: 24, background: "white", borderRadius: 4, border: `2px solid ${t.bg}` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: t.fontMono, color: "#C0392B", fontSize: 10 }}>Bearish (-1)</span>
          <span style={{ fontFamily: t.fontMono, color: sentColor, fontSize: 12, fontWeight: "bold" }}>{score.toFixed(3)}</span>
          <span style={{ fontFamily: t.fontMono, color: "#4A9B6F", fontSize: 10 }}>Bullish (+1)</span>
        </div>
      </div>
    );
  };

  const CrossAssetChart = ({ result }) => {
    if (!result?.cross_asset) return <Empty message="No cross asset data" />;
    const ca = result.cross_asset;
    const score = ca.cross_asset_score || 0;
    const scoreColor = score > 0.4 ? "#4A9B6F" : score < -0.4 ? "#C0392B" : "#E8A838";

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {[
            ["Cross Asset Score", score.toFixed(2), scoreColor],
            ["Sector", ca.sector || "—", t.textPrimary],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "16px 24px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 28, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
        <div style={{ padding: "16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{ca.signal || "—"}</p>
        </div>
      </div>
    );
  };

  const EarningsChart = ({ result }) => {
    if (!result?.earnings) return <Empty message="No earnings data" />;
    const earn = result.earnings;
    const surprise = earn.avg_surprise || 0;
    const surpriseColor = surprise > 0 ? "#4A9B6F" : "#C0392B";

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["In Blackout",    earn.in_blackout ? "YES" : "NO", earn.in_blackout ? "#C0392B" : "#4A9B6F"],
            ["Days to Earnings", earn.days_to_earnings ?? "—", t.textPrimary],
            ["Avg EPS Surprise", surprise ? `${surprise > 0 ? "+" : ""}${surprise.toFixed(2)}%` : "—", surpriseColor],
            ["Position Mult",  (earn.position_multiplier || 1).toFixed(2) + "x", t.textSilver],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px", flex: 1, minWidth: 110 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 22, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
        <div style={{ padding: "12px 16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{earn.recommendation || "—"}</p>
        </div>
      </div>
    );
  };

  const GraphChart = ({ result }) => {
    if (!result?.graph) return <Empty message="No graph data" />;
    const g = result.graph;
    const strength = g.signal_strength || 0;
    const raw = g.raw_signal || 0;
    const smooth = g.smooth_signal || 0;
    const residual = g.residual_signal || 0;
    const strengthColor = strength > 0.5 ? "#4A9B6F" : strength < -0.5 ? "#C0392B" : "#E8A838";

    const W = 400, H = 120, pad = 20;
    const signals = [
      { label: "Raw",      val: raw,      color: "#6B6B88" },
      { label: "Smooth",   val: smooth,   color: "#5BA4CF" },
      { label: "Residual", val: residual, color: "#A78BDB" },
      { label: "Strength", val: strength, color: strengthColor },
    ];
    const maxAbs = Math.max(...signals.map(s => Math.abs(s.val)), 0.01);

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "16px 24px", flex: 1 }}>
            <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>Signal Strength</p>
            <p style={{ fontFamily: t.fontDisplay, color: strengthColor, fontSize: 36, margin: 0 }}>{strength.toFixed(4)}</p>
          </div>
        </div>
        {signals.map(({ label, val, color }) => {
          const pct = (Math.abs(val) / maxAbs) * 100;
          const isNeg = val < 0;
          return (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>{label}</span>
                <span style={{ fontFamily: t.fontMono, color: isNeg ? "#C0392B" : t.textSilver, fontSize: 12 }}>{val > 0 ? "+" : ""}{val.toFixed(4)}</span>
              </div>
              <div style={{ height: 8, background: t.border, borderRadius: 4 }}>
                <div style={{ height: 8, width: `${pct}%`, background: isNeg ? "#C0392B" : color, borderRadius: 4, transition: "width 0.6s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const RegimeChart = ({ result }) => {
    if (!result?.regime) return <Empty message="No regime data" />;
    const reg = result.regime;
    const scale = (reg.bull_pct || 0) <= 1 ? 100 : 1;
    const probs = [
      { label: "Bull",     val: (reg.bull_pct || 0) * scale,     color: "#4A9B6F" },
      { label: "Sideways", val: (reg.sideways_pct || 0) * scale, color: "#F5A623" },
      { label: "Bear",     val: (reg.bear_pct || 0) * scale,     color: "#C0392B" },
    ];
    const W = 280, H = 280, cx = 140, cy = 140, r = 100;
    let startAngle = -Math.PI / 2;
    const slices = probs.map(p => {
      const angle = (p.val / 100) * 2 * Math.PI;
      const endAngle = startAngle + angle;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);
      const largeArc = angle > Math.PI ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      const midAngle = startAngle + angle / 2;
      const lx = cx + (r * 0.65) * Math.cos(midAngle);
      const ly = cy + (r * 0.65) * Math.sin(midAngle);
      startAngle = endAngle;
      return { ...p, path, lx, ly };
    });

    return (
      <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          {slices.map(s => (
            <g key={s.label}>
              <path d={s.path} fill={s.color} fillOpacity="0.85" stroke={t.bg} strokeWidth="2" />
              {s.val > 5 && (
                <text x={s.lx} y={s.ly} textAnchor="middle" fill="white" fontSize="12" fontFamily="monospace" fontWeight="bold">
                  {s.val.toFixed(0)}%
                </text>
              )}
            </g>
          ))}
          <circle cx={cx} cy={cy} r={42} fill={t.bg} />
          <text x={cx} y={cy - 6} textAnchor="middle" fill={t.textPrimary} fontSize="12" fontFamily="monospace">{reg.regime || "—"}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fill={t.textMuted} fontSize="10" fontFamily="monospace">{((reg.confidence || 0) * 100).toFixed(0)}% conf</text>
        </svg>
        <div>
          {probs.map(p => (
            <div key={p.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 12, height: 12, background: p.color, borderRadius: 2 }} />
              <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 13, width: 80 }}>{p.label}</span>
              <span style={{ fontFamily: t.fontMono, color: t.textPrimary, fontSize: 18 }}>{p.val.toFixed(1)}%</span>
            </div>
          ))}
          <div style={{ marginTop: 12, padding: "10px 14px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
            <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, margin: "0 0 2px", letterSpacing: 2, textTransform: "uppercase" }}>Multiplier</p>
            <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 24, margin: 0 }}>{reg.multiplier || "—"}x</p>
          </div>
        </div>
      </div>
    );
  };

  const TopologyChart = ({ result }) => {
    if (!result?.topology) return <Empty message="No topology data" />;
    const top = result.topology;
    const stress = top.stress_score || 0;
    const stressColor = stress > 0.7 ? "#C0392B" : stress > 0.4 ? "#E8A838" : "#4A9B6F";

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["Stress Score",   stress.toFixed(3),                  stressColor],
            ["Stress Level",   top.stress_level || "—",            t.textPrimary],
            ["Avg Correlation",(top.avg_correlation || 0).toFixed(3), t.textSilver],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "16px 24px", flex: 1 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 28, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 8px" }}>Market Stress Level</p>
        <div style={{ height: 20, background: `linear-gradient(to right, #4A9B6F, #E8A838, #C0392B)`, borderRadius: 10, marginBottom: 6, position: "relative" }}>
          <div style={{ position: "absolute", left: `${stress * 100}%`, top: -4, transform: "translateX(-50%)", width: 16, height: 28, background: "white", borderRadius: 4, border: `2px solid ${t.bg}` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontFamily: t.fontMono, color: "#4A9B6F", fontSize: 10 }}>Low Stress</span>
          <span style={{ fontFamily: t.fontMono, color: stressColor, fontSize: 12, fontWeight: "bold" }}>{(stress * 100).toFixed(1)}%</span>
          <span style={{ fontFamily: t.fontMono, color: "#C0392B", fontSize: 10 }}>High Stress</span>
        </div>

        <div style={{ padding: "12px 16px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 11, margin: 0 }}>{top.recommendation || "—"}</p>
        </div>
      </div>
    );
  };

  const MomentumChart = ({ result }) => {
    if (!result?.momentum) return <Empty message="No momentum data" />;
    const m = result.momentum;
    const score = m.momentum_score || 0;
    const scoreColor = score > 0.3 ? "#4A9B6F" : score < -0.3 ? "#C0392B" : "#E8A838";

    const bars = [
      { label: "ROC 5D",            val: m.roc_5  || 0,             color: "#5BA4CF" },
      { label: "ROC 20D",           val: m.roc_20 || 0,             color: "#7B5EA7" },
      { label: "ROC 60D",           val: m.roc_60 || 0,             color: "#A78BDB" },
      { label: "Acceleration",      val: m.acceleration || 0,       color: "#E8A838" },
      { label: "Relative Strength", val: m.relative_strength || 0,  color: "#4A9B6F" },
    ];
    const maxAbs = Math.max(...bars.map(b => Math.abs(b.val)), 0.01);

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["Momentum Score",  score.toFixed(3),                          scoreColor],
            ["ADX",             (m.adx || 0).toFixed(1),                   (m.adx || 0) > 30 ? "#4A9B6F" : "#E8A838"],
            ["52W Proximity",   `${((m.proximity_52w || 0) * 100).toFixed(1)}%`, (m.proximity_52w || 0) > 0.9 ? "#4A9B6F" : "#E8A838"],
            ["Sector",          m.sector || "—",                           t.textSilver],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px", flex: 1, minWidth: 110 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 22, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
        {bars.map(({ label, val, color }) => {
          const pct = (Math.abs(val) / maxAbs) * 100;
          const isNeg = val < 0;
          return (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>{label}</span>
                <span style={{ fontFamily: t.fontMono, color: isNeg ? "#C0392B" : t.textSilver, fontSize: 12 }}>{val > 0 ? "+" : ""}{(val * 100).toFixed(2)}%</span>
              </div>
              <div style={{ height: 6, background: t.border, borderRadius: 3 }}>
                <div style={{ height: 6, width: `${pct}%`, background: isNeg ? "#C0392B" : color, borderRadius: 3, transition: "width 0.6s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const MacroChart = ({ result }) => {
    if (!result?.macro) return <Empty message="No macro data" />;
    const m = result.macro;
    const toNum = (v) => isNaN(parseFloat(v)) ? 0 : parseFloat(v);
    const signals = [
      { label: "Yield Signal",    val: toNum(m.yield_signal)    },
      { label: "VIX Signal",      val: toNum(m.vix_signal)      },
      { label: "Dollar Signal",   val: toNum(m.dxy_signal)      },
      { label: "Credit Signal",   val: toNum(m.credit_signal)   },
      { label: "Momentum Signal", val: toNum(m.momentum_signal) },
    ];

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["Macro Score",  toNum(m.macro_score).toFixed(3),  toNum(m.macro_score) > 0 ? "#4A9B6F" : "#C0392B"],
            ["Regime",       m.macro_regime || "—",            t.textPrimary],
            ["Yield Spread", m.yield_spread ? `${(toNum(m.yield_spread) * 100).toFixed(2)}%` : "—", t.textSilver],
            ["VIX",          m.vix || "—",                     toNum(m.vix) > 25 ? "#C0392B" : "#4A9B6F"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px", flex: 1, minWidth: 110 }}>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>{l}</p>
              <p style={{ fontFamily: t.fontDisplay, color: c, fontSize: 22, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>
        {signals.map(({ label, val }) => {
          const color = val > 0 ? "#4A9B6F" : val < 0 ? "#C0392B" : "#6B6B88";
          return (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>{label}</span>
                <span style={{ fontFamily: t.fontMono, color, fontSize: 12 }}>{val > 0 ? "+" : ""}{val.toFixed(2)}</span>
              </div>
              <div style={{ height: 6, background: t.border, borderRadius: 3 }}>
                <div style={{ height: 6, width: `${Math.min(Math.abs(val) * 100, 100)}%`, background: color, borderRadius: 3, maxWidth: "100%", transition: "width 0.6s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderChart = (result, layer) => {
    if (!result || result.error) return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 13 }}>{result?.error || "Enter a ticker to scan"}</p>
      </div>
    );
    switch (layer) {
      case "prediction":    return <PredictionChart result={result} />;
      case "ensemble":      return <EnsembleChart result={result} />;
      case "tcn":           return <TCNChart result={result} />;
      case "xgboost":       return <XGBoostChart result={result} />;
      case "options":       return <OptionsChart result={result} />;
      case "insider":       return <InsiderChart result={result} />;
      case "short_interest":return <ShortInterestChart result={result} />;
      case "sentiment":     return <SentimentChart result={result} />;
      case "cross_asset":   return <CrossAssetChart result={result} />;
      case "earnings":      return <EarningsChart result={result} />;
      case "graph":         return <GraphChart result={result} />;
      case "momentum":      return <MomentumChart result={result} />;
      case "macro":         return <MacroChart result={result} />;
      case "regime":        return <RegimeChart result={result} />;
      case "topology":      return <TopologyChart result={result} />;
      default:              return <Empty message="Select a layer" />;
    }
  };
  
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, margin: 0 }}>
          Stock Scanner
        </h2>
        <button
          onClick={() => setCompareMode(!compareMode)}
          style={{ background: compareMode ? t.purple : "transparent", color: compareMode ? t.textPrimary : t.textMuted, border: `1px solid ${compareMode ? t.purple : t.border}`, padding: "8px 18px", fontSize: 10, fontFamily: t.fontBody, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer" }}
        >
          {compareMode ? "◧ Compare On" : "◧ Compare"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
        {LAYERS.map(layer => (
          <button key={layer.id} onClick={() => setActiveLayer(layer.id)} style={{ background: activeLayer === layer.id ? layer.color : "transparent", color: activeLayer === layer.id ? "white" : t.textMuted, border: `1px solid ${activeLayer === layer.id ? layer.color : t.border}`, padding: "6px 14px", fontSize: 10, fontFamily: t.fontBody, fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", transition: "all 0.15s", borderBottom: activeLayer === layer.id ? `2px solid white` : `1px solid ${t.border}` }}>
            {layer.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <ScanPanel ticker={tickerA} setTicker={setTickerA} result={resultA} loading={loadingA} onScan={() => scan(tickerA, setResultA, setLoadingA)} label="Enter ticker..." activeLayer={activeLayer} renderChart={renderChart} />
        {compareMode && (
          <ScanPanel ticker={tickerB} setTicker={setTickerB} result={resultB} loading={loadingB} onScan={() => scan(tickerB, setResultB, setLoadingB)} label="Compare ticker..." activeLayer={activeLayer} renderChart={renderChart} />
        )}
      </div>
    </div>
  );
}

// ─── Performance Tab ──────────────────────────────────────────────────────────
function PerformanceTab({ token }) {
  const { data: positions, loading: pLoad } = useAPI("/api/investing/positions", token);
  const { data: trades,    loading: tLoad } = useAPI("/api/investing/trades",    token);
  const { data: monitorData } = useAPI("/api/monitor/status", token);

  const pos = Array.isArray(positions) ? positions : [];
  const trd = Array.isArray(trades)    ? trades    : [];

  const longs  = pos.filter(p => p.side === "LONG");
  const shorts = pos.filter(p => p.side === "SHORT");

  const totalDeployed = pos.reduce((s, p) => s + (parseFloat(p.dollar_amount) || 0), 0);
  const avgConviction = pos.length ? pos.reduce((s, p) => s + (parseFloat(p.conviction_score) || 0), 0) / pos.length : 0;
  const avgBeta       = pos.reduce((s, p) => s + (parseFloat(p.vol_scalar) || 0), 0) / (pos.length || 1);

  // Build equity curve from trades or use placeholder
  const hasTradeData = trd.length > 0 && trd.some(t => t.pnl);
  const equityData = hasTradeData ? (() => {
    let equity = 1000000;
    return trd.map((tr, i) => {
      equity += parseFloat(tr.pnl || 0);
      return { i, equity, date: tr.date || tr.timestamp || `T${i}` };
    });
  })() : Array.from({ length: 30 }, (_, i) => ({ i, equity: null, date: `Day ${i+1}` }));

  // Placeholder Monte Carlo paths
  const mcPaths = Array.from({ length: 20 }, (_, pathIdx) => {
    let val = 1000000;
    return Array.from({ length: 60 }, (_, i) => {
      val = val * (1 + (Math.random() - 0.48) * 0.012);
      return val;
    });
  });

  const W = 800, H = 260;
  const pad = { top: 20, right: 30, bottom: 40, left: 80 };
  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;

  // Equity chart SVG
  const EquityChart = () => {
    if (!hasTradeData) return (
      <div style={{ height: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: t.bgDeep, border: `1px solid ${t.border}` }}>
        <p style={{ fontFamily: t.fontDisplay, color: t.textMuted, fontSize: 22, margin: "0 0 8px" }}>Awaiting Trade Data</p>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>Equity curve will appear once the bot starts executing trades</p>
      </div>
    );

    const maxEq = Math.max(...equityData.map(d => d.equity));
    const minEq = Math.min(...equityData.map(d => d.equity));
    const range = maxEq - minEq || 1;
    const xS = (i) => pad.left + (i / (equityData.length - 1)) * chartW;
    const yS = (v) => pad.top + chartH - ((v - minEq) / range) * chartH;
    const path = equityData.map((d, i) => `${i === 0 ? "M" : "L"} ${xS(i)} ${yS(d.equity)}`).join(" ");
    const fill = equityData.map((d, i) => `${i === 0 ? "M" : "L"} ${xS(i)} ${yS(d.equity)}`).join(" ") + ` L ${xS(equityData.length-1)} ${pad.top + chartH} L ${xS(0)} ${pad.top + chartH} Z`;

    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        {Array.from({ length: 5 }).map((_, i) => {
          const y = pad.top + (i / 4) * chartH;
          const v = maxEq - (i / 4) * range;
          return (
            <g key={i}>
              <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke={t.border} strokeWidth="1" />
              <text x={pad.left - 8} y={y + 4} textAnchor="end" fill={t.textMuted} fontSize="10" fontFamily="monospace">${(v/1000).toFixed(0)}k</text>
            </g>
          );
        })}
        <path d={fill} fill={t.purple} fillOpacity="0.06" />
        <path d={path} stroke={t.purple} strokeWidth="2" fill="none" />
        <line x1={pad.left} y1={yS(1000000)} x2={W - pad.right} y2={yS(1000000)} stroke={t.textMuted} strokeWidth="1" strokeDasharray="4,4" />
      </svg>
    );
  };

  // Monte Carlo chart SVG
  const MonteCarloChart = () => {
    const allVals = mcPaths.flat();
    const maxV = Math.max(...allVals);
    const minV = Math.min(...allVals);
    const range = maxV - minV || 1;
    const points = 60;
    const xS = (i) => pad.left + (i / (points - 1)) * chartW;
    const yS = (v) => pad.top + chartH - ((v - minV) / range) * chartH;

    return (
      <div style={{ position: "relative" }}>
        <div style={{ position: "absolute", top: 12, left: pad.left, fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>
          Monte Carlo — 20 Simulated Paths (60 Days)
        </div>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
          {Array.from({ length: 5 }).map((_, i) => {
            const y = pad.top + (i / 4) * chartH;
            const v = maxV - (i / 4) * range;
            return (
              <g key={i}>
                <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke={t.border} strokeWidth="1" />
                <text x={pad.left - 8} y={y + 4} textAnchor="end" fill={t.textMuted} fontSize="10" fontFamily="monospace">${(v/1000).toFixed(0)}k</text>
              </g>
            );
          })}
          {mcPaths.map((path, pi) => {
            const d = path.map((v, i) => `${i === 0 ? "M" : "L"} ${xS(i)} ${yS(v)}`).join(" ");
            const finalVal = path[path.length - 1];
            const color = finalVal > 1000000 ? t.positive : t.rubyLight;
            return <path key={pi} d={d} stroke={color} strokeWidth="1" fill="none" strokeOpacity="0.3" />;
          })}
          <line x1={pad.left} y1={yS(1000000)} x2={W - pad.right} y2={yS(1000000)} stroke="white" strokeWidth="1" strokeDasharray="4,4" strokeOpacity="0.4" />
          {/* X-axis time labels */}
          {[0, 10, 20, 30, 40, 50, 59].map(i => (
            <g key={i}>
              <line x1={xS(i)} y1={pad.top + chartH} x2={xS(i)} y2={pad.top + chartH + 4} stroke={t.border} strokeWidth="1" />
              <text x={xS(i)} y={pad.top + chartH + 14} textAnchor="middle" fill={t.textMuted} fontSize="9" fontFamily="monospace">
                {i === 0 ? "Now" : `M${Math.round(i/59 * 2)}`}
              </text>
            </g>
          ))}
          {/* Right y-axis: % from baseline */}
          {Array.from({ length: 5 }).map((_, i) => {
            const y = pad.top + (i / 4) * chartH;
            const v = maxV - (i / 4) * range;
            const pct = ((v - 1000000) / 1000000 * 100).toFixed(1);
            return (
              <text key={i} x={W - pad.right - 4} y={y + 4} textAnchor="end" fill={t.textMuted} fontSize="9" fontFamily="monospace">
                {pct > 0 ? "+" : ""}{pct}%
              </text>
            );
          })}
        </svg>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, margin: "4px 0 0", fontStyle: "italic" }}>
          * Simulated paths based on current vol scalar. Will use real trade distribution once history exists.
        </p>
      </div>
    );
  };

  // Rolling Sharpe placeholder
  const SharpeChart = () => {
    if (!hasTradeData) return (
      <div style={{ height: 160, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>Rolling Sharpe will appear once trade history exists</p>
      </div>
    );
    return null;
  };

  // Drawdown placeholder
  const DrawdownChart = () => {
    if (!hasTradeData) return (
      <div style={{ height: 160, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>Drawdown chart will appear once trade history exists</p>
      </div>
    );
    return null;
  };

  // Win rate by signal count
  const signalDist = pos.reduce((acc, p) => {
    const s = parseInt(p.signals_agreeing) || 0;
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, marginBottom: 28 }}>
        Bot Performance
      </h2>

      {/* Overfitting Monitor Status */}
      {monitorData && (
        <div style={{
          background: t.surface,
          border: `2px solid ${
            monitorData.level === "HALT"     ? "#C0392B" :
            monitorData.level === "CAUTION"  ? "#E8A838" :
            monitorData.level === "WARNING"  ? "#E8A838" :
            monitorData.level === "NORMAL"   ? "#4A9B6F" : t.border
          }`,
          padding: "16px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 12, height: 12, borderRadius: "50%",
              background:
                monitorData.level === "HALT"    ? "#C0392B" :
                monitorData.level === "CAUTION" ? "#E8A838" :
                monitorData.level === "WARNING" ? "#E8A838" :
                monitorData.level === "NORMAL"  ? "#4A9B6F" : t.textMuted,
              boxShadow: monitorData.level === "HALT" ? "0 0 8px #C0392B" :
                         monitorData.level === "NORMAL" ? "0 0 8px #4A9B6F" : "none",
            }} />
            <div>
              <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 2px" }}>Overfitting Monitor</p>
              <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 20, margin: 0 }}>
                {monitorData.level || "INACTIVE"}
              </p>
            </div>
          </div>
          <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12, margin: 0, flex: 1, textAlign: "center" }}>
            {monitorData.action || "Run bot pipeline to activate monitor"}
          </p>
          {monitorData.metrics && Object.keys(monitorData.metrics).length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(monitorData.metrics).map(([key, reason]) => (
                <div key={key} style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "4px 10px" }}>
                  <span style={{ fontFamily: t.fontMono, color: t.textMuted, fontSize: 9, letterSpacing: 1, textTransform: "uppercase" }}>
                    {key.replace(/_/g, " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard label="Total Deployed"  value={`$${totalDeployed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
        <StatCard label="Open Positions"  value={pos.length} />
        <StatCard label="Longs"           value={longs.length}  accent={t.positive} />
        <StatCard label="Shorts"          value={shorts.length} accent={t.rubyLight} />
        <StatCard label="Avg Conviction"  value={`${(avgConviction * 100).toFixed(1)}%`} mono />
        <StatCard label="Avg Vol Scalar"  value={avgBeta.toFixed(3)} mono />
        <StatCard label="Total Trades"    value={trd.length} mono />
      </div>

      {/* Equity curve */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", marginBottom: 16 }}>
        <SectionHeader title="Equity Curve vs Baseline" />
        <EquityChart />
      </div>

      {/* Monte Carlo */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px", marginBottom: 16 }}>
        <SectionHeader title="Monte Carlo Simulation" />
        <MonteCarloChart />
      </div>

      {/* Signal Distribution + Sharpe/Drawdown layout */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "stretch" }}>
        
        {/* Left 2/3 — Signal Agreement */}
        <div style={{ flex: 2, background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px" }}>
          <SectionHeader title="Signal Agreement Distribution" />
          <div style={{ display: "flex", gap: 32, alignItems: "flex-start", marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 200, marginBottom: 8 }}>
                {Array.from({ length: 14 }, (_, i) => i).map(n => {
                  const count = signalDist[n] || 0;
                  const maxCount = Math.max(...Object.values(signalDist), 1);
                  const h = count > 0 ? Math.max((count / maxCount) * 100, 8) : 0;
                  const color = "#7B5EA7";
                  return (
                    <div key={n} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }}>
                      {count > 0 && <span style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 10 }}>{count}</span>}
                      <div style={{ width: "100%", height: `${h}%`, background: color, borderRadius: "3px 3px 0 0", minHeight: count > 0 ? 4 : 0, transition: "height 0.4s ease" }} />
                      <span style={{ fontFamily: t.fontMono, color: t.textMuted, fontSize: 10 }}>{n}</span>
                    </div>
                  );
                })}
              </div>
              {/* Bell curve overlay */}
            {(() => {
              const W = 600, H = 60;
              const mean = 5, std = 2.5;
              const points = Array.from({ length: 14 }, (_, i) => {
                const y = Math.exp(-0.5 * Math.pow((i - mean) / std, 2));
                const x = (i / 13) * W;
                return `${x},${H - y * (H - 4)}`;
              });
              const path = "M " + points.join(" L ");
              return (
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 4, opacity: 0.5 }}>
                  <path d={path} stroke="#4A9B6F" strokeWidth="2" fill="none" strokeDasharray="4,3" />
                  <text x={W - 4} y={H - 6} textAnchor="end" fill="#4A9B6F" fontSize="9" fontFamily="monospace">— IDEAL DISTRIBUTION</text>
                </svg>
              );
            })()}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
                <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Signals Agreeing (0–13)</span>
              </div>
            </div>

            {/* Stats + curve fit */}
            <div style={{ width: 200, display: "flex", flexDirection: "column", gap: 12 }}>
              {(() => {
                const total = pos.length;
                const avgSignals = total ? pos.reduce((s, p) => s + (parseInt(p.signals_agreeing) || 0), 0) / total : 0;
                const highConv = pos.filter(p => parseInt(p.signals_agreeing) >= 8).length;
                const highConvPct = total ? highConv / total : 0;
                const suspiciouslyHigh = highConvPct > 0.5;
                return (
                  <>
                    <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px" }}>
                      <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>Avg Signal Agreement</p>
                      <p style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 28, margin: 0 }}>{avgSignals.toFixed(1)}</p>
                    </div>
                    <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, padding: "12px 16px" }}>
                      <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>High Conviction (8+)</p>
                      <p style={{ fontFamily: t.fontDisplay, color: highConvPct > 0.4 ? "#E8A838" : t.positive, fontSize: 28, margin: 0 }}>{highConv} <span style={{ fontSize: 14, color: t.textMuted }}>/ {total}</span></p>
                    </div>
                    <div style={{ background: t.bgDeep, border: `1px solid ${suspiciouslyHigh ? "#C0392B" : t.border}`, padding: "12px 16px" }}>
                      <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px" }}>Curve Fit Risk</p>
                      <p style={{ fontFamily: t.fontDisplay, color: suspiciouslyHigh ? "#C0392B" : "#4A9B6F", fontSize: 20, margin: 0 }}>
                        {suspiciouslyHigh ? "⚠ HIGH" : "✓ NORMAL"}
                      </p>
                      <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, margin: "4px 0 0" }}>
                        {suspiciouslyHigh ? "Too many signals agreeing — possible overfit" : "Signal distribution looks healthy"}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right 1/3 — Sharpe + Drawdown stacked */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ flex: 1, background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px" }}>
            <SectionHeader title="Rolling Sharpe Ratio" />
            <SharpeChart />
          </div>
          <div style={{ flex: 1, background: t.surface, border: `1px solid ${t.border}`, padding: "20px 24px" }}>
            <SectionHeader title="Drawdown" />
            <DrawdownChart />
          </div>
        </div>

      </div>

      {/* Positions table */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, marginBottom: 16 }}>
        <SectionHeader title="Current Positions" />
        {pLoad ? <Loader /> : (
          <DataTable
            cols={["Ticker", "Side", "Action", "Conviction", "Signals", "Size %", "$ Amount", "Price"]}
            rows={pos.map(p => [
              p.ticker,
              <span style={{ color: p.side === "LONG" ? t.positive : t.rubyLight, fontFamily: t.fontMono, fontSize: 12 }}>{p.side}</span>,
              <ActionBadge action={p.action} />,
              `${((parseFloat(p.conviction_score) || 0) * 100).toFixed(1)}%`,
              `${p.signals_agreeing} / 13`,
              `${((parseFloat(p.position_size_pct) || 0) * 100).toFixed(2)}%`,
              `$${(parseFloat(p.dollar_amount) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              `$${parseFloat(p.current_price).toFixed(2)}`,
            ])}
          />
        )}
      </div>

      {/* Trade history */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
        <SectionHeader title="Trade History" />
        {tLoad ? <Loader /> : trd.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 13 }}>No trade history yet — will populate once bot starts executing.</p>
          </div>
        ) : (
          <DataTable
            cols={["Ticker", "Side", "Action", "Strategy", "Entry", "Exit", "P&L", "%", "Outcome", "Barrier", "Days", "Date"]}
            rows={trd.map(tr => {
              const pnl     = parseFloat(tr.pnl || 0);
              const pct     = parseFloat(tr.pct_change || 0);
              const outcome = tr.outcome;
              const outcomeColor =
                outcome === "WIN"     ? t.positive :
                outcome === "LOSS"    ? t.rubyLight :
                outcome === "NEUTRAL" ? "#E8A838" : t.textMuted;
              const strategyColor = {
                short:        "#C0392B",
                short_medium: "#E8A838",
                medium_long:  "#4A9B6F",
                long:         t.gold,
              }[tr.strategy] || t.textMuted;

              return [
                <span style={{ color: t.gold, fontFamily: t.fontMono, fontSize: 12 }}>{tr.ticker}</span>,
                <span style={{ color: tr.side === "LONG" ? t.positive : t.rubyLight, fontFamily: t.fontMono, fontSize: 11 }}>{tr.side || "—"}</span>,
                <span style={{ fontFamily: t.fontBody, color: t.textSilver, fontSize: 11 }}>{tr.action}</span>,
                <span style={{
                  background: `${strategyColor}18`,
                  color: strategyColor,
                  border: `1px solid ${strategyColor}44`,
                  padding: "2px 8px", fontSize: 9,
                  fontFamily: t.fontBody, fontWeight: t.weightSub,
                  letterSpacing: 1, borderRadius: 2,
                  textTransform: "uppercase",
                }}>{(tr.strategy || "—").replace("_", "-")}</span>,
                <span style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 11 }}>{tr.entry_price ? `$${parseFloat(tr.entry_price).toFixed(2)}` : "—"}</span>,
                <span style={{ fontFamily: t.fontMono, color: t.textSilver, fontSize: 11 }}>{tr.exit_price ? `$${parseFloat(tr.exit_price).toFixed(2)}` : "—"}</span>,
                <span style={{ color: pnl >= 0 ? t.positive : t.rubyLight, fontFamily: t.fontMono, fontSize: 12 }}>
                  {pnl !== 0 ? `${pnl >= 0 ? "+" : ""}$${Math.abs(pnl).toFixed(2)}` : "—"}
                </span>,
                <span style={{ color: pct >= 0 ? t.positive : t.rubyLight, fontFamily: t.fontMono, fontSize: 12 }}>
                  {pct !== 0 ? `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%` : "—"}
                </span>,
                outcome ? (
                  <span style={{
                    background: `${outcomeColor}18`,
                    color: outcomeColor,
                    border: `1px solid ${outcomeColor}44`,
                    padding: "2px 8px", fontSize: 9,
                    fontFamily: t.fontBody, fontWeight: t.weightSub,
                    letterSpacing: 1, borderRadius: 2,
                  }}>{outcome}</span>
                ) : <span style={{ color: t.textMuted, fontSize: 11 }}>OPEN</span>,
                <span style={{ color: t.textMuted, fontSize: 11 }}>{tr.barrier_hit ? tr.barrier_hit.replace("_", " ") : "—"}</span>,
                <span style={{ fontFamily: t.fontMono, color: t.textMuted, fontSize: 11 }}>{tr.days_held || "—"}</span>,
                <span style={{ color: t.textMuted, fontSize: 11 }}>{tr.timestamp ? new Date(tr.timestamp).toLocaleDateString() : "—"}</span>,
              ];
            })}
          />
        )}
      </div>
    </div>
  );
}

// ─── Clients Tab ──────────────────────────────────────────────────────────────
function ClientsTab({ token }) {
  const { data: rawClients, loading, refetch } = useAPI("/api/admin/clients", token);
  const clients = Array.isArray(rawClients) ? rawClients : [];
  const [showForm,        setShowForm]        = useState(false);
  const [showAdvanced,    setShowAdvanced]    = useState(false);
  const [editClient,      setEditClient]      = useState(null);
  const [saving,          setSaving]          = useState(false);

  const DEFAULT_STRATEGY = { short: 25, short_medium: 35, medium_long: 25, long: 15 };

  const [form, setForm] = useState({
    username: "", password: "", name: "", email: "",
    phone: "", services: [], tax_bracket: 0.22,
    state_tax: 0.032, ltcg_rate: 0.0,
    risk_tolerance: "moderate", notes: "",
    strategy_allocation: { ...DEFAULT_STRATEGY },
  });

  const STRATEGY_META = {
    short:        { label: "Short",        color: "#C0392B", range: "Days–6 weeks",   price: "$2–$20"    },
    short_medium: { label: "Short-Medium", color: "#E8A838", range: "2–6 months",     price: "$15–$100"  },
    medium_long:  { label: "Medium-Long",  color: "#4A9B6F", range: "6–12 months",    price: "$50–$300"  },
    long:         { label: "Long",         color: t.gold,    range: "1–2 years",      price: "$100+"     },
  };

  // Slider logic — adjusting one redistributes others proportionally
  const adjustSlider = (key, newVal) => {
    const alloc = { ...form.strategy_allocation };
    const oldVal = alloc[key];
    const diff   = newVal - oldVal;
    const others = Object.keys(alloc).filter(k => k !== key);
    const otherTotal = others.reduce((s, k) => s + alloc[k], 0);

    if (otherTotal === 0) return;

    // Redistribute diff proportionally among others
    let remaining = 100 - newVal;
    const newAlloc = { ...alloc, [key]: newVal };
    others.forEach((k, i) => {
      if (i === others.length - 1) {
        newAlloc[k] = Math.max(0, remaining);
      } else {
        const share = Math.round((alloc[k] / otherTotal) * (100 - newVal));
        newAlloc[k] = Math.max(0, share);
        remaining -= newAlloc[k];
      }
    });

    // Ensure total = 100
    const total = Object.values(newAlloc).reduce((s, v) => s + v, 0);
    if (total !== 100) {
      const lastKey = others[others.length - 1];
      newAlloc[lastKey] = Math.max(0, newAlloc[lastKey] + (100 - total));
    }

    setForm(f => ({ ...f, strategy_allocation: newAlloc }));
  };

  const toggleService = (s) => {
    setForm(f => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter(x => x !== s)
        : [...f.services, s],
    }));
  };

  const openAdd = () => {
    setEditClient(null);
    setShowAdvanced(false);
    setForm({
      username: "", password: "", name: "", email: "", phone: "",
      services: [], tax_bracket: 0.22, state_tax: 0.032, ltcg_rate: 0.0,
      risk_tolerance: "moderate", notes: "",
      strategy_allocation: { ...DEFAULT_STRATEGY },
    });
    setShowForm(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    setShowAdvanced(false);
    setForm({
      username:            client.username,
      password:            "",
      name:                client.name || "",
      email:               client.email || "",
      phone:               client.phone || "",
      services:            client.services || [],
      tax_bracket:         client.tax_bracket || 0.22,
      state_tax:           client.state_tax || 0.032,
      ltcg_rate:           client.ltcg_rate || 0.0,
      risk_tolerance:      client.risk_tolerance || "moderate",
      notes:               client.notes || "",
      strategy_allocation: client.strategy_allocation || { ...DEFAULT_STRATEGY },
    });
    setShowForm(true);
  };

  const saveClient = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editClient) {
        const body = { ...form };
        if (!body.password) delete body.password;
        await fetch(`${API_BASE}/api/admin/clients/${editClient.username}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else {
        await fetch(`${API_BASE}/api/admin/clients`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }
      setShowForm(false);
      setEditClient(null);
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", background: t.bgDeep, border: "none",
    borderBottom: `1px solid ${t.borderGold}`, color: t.textPrimary,
    padding: "8px 4px", fontSize: 13, fontFamily: t.fontMono,
    outline: "none", boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontFamily: t.fontBody, fontSize: 9,
    color: t.textMuted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6,
  };

  const total = Object.values(form.strategy_allocation).reduce((s, v) => s + v, 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h2 style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 28, color: t.textPrimary, letterSpacing: 1, margin: 0 }}>
          Client Accounts
        </h2>
        <button onClick={openAdd} style={{
          background: "transparent", color: t.gold,
          border: `1px solid ${t.borderGold}`,
          padding: "8px 18px", fontSize: 10, fontFamily: t.fontBody,
          fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
        }}>+ Add Client</button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div style={{ background: t.surface, border: `1px solid ${t.borderGold}`, borderTop: `2px solid ${t.gold}`, padding: "28px 32px", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <p style={{ fontFamily: t.fontDisplay, fontWeight: t.weightDisplay, fontSize: 18, color: t.textPrimary, margin: 0 }}>
              {editClient ? `Editing — ${editClient.name}` : "New Client"}
            </p>
            <button onClick={() => { setShowForm(false); setEditClient(null); }} style={{
              background: "transparent", color: t.textMuted, border: "none", fontSize: 18, cursor: "pointer",
            }}>✕</button>
          </div>

          {/* Basic Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={inputStyle} />
            </div>
            {!editClient && (
              <div>
                <label style={labelStyle}>Username *</label>
                <input value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} style={inputStyle} />
              </div>
            )}
            <div>
              <label style={labelStyle}>{editClient ? "New Password (leave blank to keep)" : "Password *"}</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Risk Tolerance</label>
              <select value={form.risk_tolerance} onChange={e => setForm(f => ({...f, risk_tolerance: e.target.value}))} style={{...inputStyle, padding: "8px 4px"}}>
                <option value="conservative">Conservative</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </div>
          </div>

          {/* Services */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Services</label>
            <div style={{ display: "flex", gap: 12 }}>
              {["investing", "tax"].map(s => (
                <button key={s} onClick={() => toggleService(s)} style={{
                  background: form.services.includes(s) ? (s === "investing" ? "rgba(201,168,76,0.15)" : "rgba(125,21,37,0.15)") : "transparent",
                  color: form.services.includes(s) ? (s === "investing" ? t.gold : t.primarySub) : t.textMuted,
                  border: `1px solid ${form.services.includes(s) ? (s === "investing" ? t.borderGold : t.primary) : t.border}`,
                  padding: "8px 20px", fontSize: 11, fontFamily: t.fontBody,
                  fontWeight: t.weightSub, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                }}>
                  {s === "investing" ? "Wayne Capital" : "Wayne Tax & Advisory"}
                </button>
              ))}
            </div>
          </div>

          {/* Strategy Allocation Sliders */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Strategy Allocation</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontFamily: t.fontMono, fontSize: 11,
                  color: total === 100 ? t.positive : "#C0392B",
                }}>
                  {total}% allocated
                </span>
                {total !== 100 && (
                  <span style={{ color: "#C0392B", fontSize: 10, fontFamily: t.fontBody }}>
                    must equal 100%
                  </span>
                )}
              </div>
            </div>

            {/* Stacked bar visualization */}
            <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 20, gap: 1 }}>
              {Object.entries(form.strategy_allocation).map(([key, val]) => (
                <div key={key} style={{
                  width: `${val}%`, background: STRATEGY_META[key].color,
                  transition: "width 0.3s ease",
                  minWidth: val > 0 ? 2 : 0,
                }} />
              ))}
            </div>

            {/* Individual sliders */}
            {Object.entries(STRATEGY_META).map(([key, meta]) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: meta.color }} />
                    <span style={{ fontFamily: t.fontBody, fontWeight: t.weightSub, color: t.textPrimary, fontSize: 12 }}>
                      {meta.label}
                    </span>
                    <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10 }}>
                      {meta.range} · {meta.price}
                    </span>
                  </div>
                  <span style={{ fontFamily: t.fontMono, color: meta.color, fontSize: 14, fontWeight: t.weightDisplay, minWidth: 40, textAlign: "right" }}>
                    {form.strategy_allocation[key]}%
                  </span>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={form.strategy_allocation[key]}
                    onChange={e => adjustSlider(key, parseInt(e.target.value))}
                    style={{
                      width: "100%",
                      accentColor: meta.color,
                      cursor: "pointer",
                      height: 4,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Advanced Settings Toggle */}
          <div style={{ marginBottom: 20 }}>
            <button onClick={() => setShowAdvanced(!showAdvanced)} style={{
              background: "transparent", color: t.textMuted,
              border: `1px solid ${t.border}`, padding: "8px 16px",
              fontSize: 10, fontFamily: t.fontBody, letterSpacing: 2,
              textTransform: "uppercase", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
              Advanced Tax Settings
            </button>
          </div>

          {showAdvanced && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20, padding: "20px", background: t.bgDeep, border: `1px solid ${t.border}` }}>
              <div>
                <label style={labelStyle}>Federal Bracket</label>
                <input type="number" step="0.01" value={form.tax_bracket} onChange={e => setForm(f => ({...f, tax_bracket: parseFloat(e.target.value)}))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>State Tax</label>
                <input type="number" step="0.001" value={form.state_tax} onChange={e => setForm(f => ({...f, state_tax: parseFloat(e.target.value)}))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>LTCG Rate</label>
                <input type="number" step="0.01" value={form.ltcg_rate} onChange={e => setForm(f => ({...f, ltcg_rate: parseFloat(e.target.value)}))} style={inputStyle} />
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3} style={{...inputStyle, resize: "vertical", padding: "8px"}} />
          </div>

          <button onClick={saveClient} disabled={saving || !form.name || (!editClient && !form.username) || total !== 100} style={{
            background: total === 100 ? t.goldGradient : t.border,
            color: total === 100 ? "#0A0A0A" : t.textMuted,
            border: "none", padding: "12px 32px", fontSize: 11,
            fontFamily: t.fontBody, fontWeight: t.weightDisplay,
            letterSpacing: 3, textTransform: "uppercase",
            cursor: (saving || total !== 100) ? "not-allowed" : "pointer",
          }}>
            {saving ? "Saving..." : editClient ? "Save Changes" : "Create Client"}
          </button>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard label="Total Clients"   value={clients.length} />
        <StatCard label="Capital Clients" value={clients.filter(c => c.services?.includes("investing")).length} accent={t.gold} />
        <StatCard label="Tax Clients"     value={clients.filter(c => c.services?.includes("tax")).length} accent={t.primary} />
        <StatCard label="Both Services"   value={clients.filter(c => c.services?.includes("investing") && c.services?.includes("tax")).length} />
      </div>

      {/* Clients table */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
        <SectionHeader title="All Clients" />
        {loading ? <Loader /> : (
          <DataTable
            cols={["Name", "Services", "Allocation", "Email", "Username", "Risk", "Actions"]}
            rows={clients.map(c => {
              const alloc = c.strategy_allocation || DEFAULT_STRATEGY;
              return [
                <span style={{ color: t.gold, fontFamily: t.fontMono }}>{c.name}</span>,
                <div style={{ display: "flex", gap: 4 }}>
                  {(c.services || []).map(s => (
                    <span key={s} style={{
                      background: s === "investing" ? "rgba(201,168,76,0.12)" : "rgba(125,21,37,0.12)",
                      color: s === "investing" ? t.gold : t.primarySub,
                      border: `1px solid ${s === "investing" ? t.borderGold : t.primary}`,
                      padding: "2px 8px", fontSize: 10, fontFamily: t.fontBody,
                      fontWeight: t.weightSub, letterSpacing: 1, borderRadius: 2,
                    }}>{s.toUpperCase()}</span>
                  ))}
                </div>,
                <div style={{ display: "flex", gap: 2, alignItems: "center", width: 120 }}>
                  {Object.entries(alloc).map(([key, val]) => (
                    <div key={key} title={`${key}: ${val}%`} style={{
                      width: `${val}%`, height: 6, borderRadius: 2,
                      background: { short: "#C0392B", short_medium: "#E8A838", medium_long: "#4A9B6F", long: t.gold }[key],
                      minWidth: val > 0 ? 2 : 0,
                    }} />
                  ))}
                </div>,
                <span style={{ color: t.textMuted, fontSize: 12 }}>{c.email}</span>,
                <span style={{ fontFamily: t.fontMono, fontSize: 12, color: t.textSilver }}>{c.username}</span>,
                <span style={{ color: t.textSilver, fontSize: 12 }}>{c.risk_tolerance}</span>,
                <button onClick={() => openEdit(c)} style={{
                  background: "transparent", color: t.gold,
                  border: `1px solid ${t.borderGold}`, padding: "4px 12px",
                  fontSize: 10, fontFamily: t.fontBody, letterSpacing: 1,
                  textTransform: "uppercase", cursor: "pointer",
                }}>Edit</button>,
              ];
            })}
          />
        )}
      </div>
    </div>
  );
}

// ─── Pipeline Tab ─────────────────────────────────────────────────────────────
function PipelineTab({ token }) {
  const { data, loading, refetch } = useAPI("/api/pipeline/log", token);

  const columns = data?.[0]
    ? Object.keys(data[0]).map(k => ({
        key: k, label: k,
        mono: typeof data[0][k] === "number",
        render: typeof data[0][k] === "number"
          ? v => v?.toFixed ? v.toFixed(3) : v
          : undefined,
      }))
    : [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, margin: 0 }}>
          Pipeline Log
        </h2>
        <button onClick={refetch} style={{
          background: "transparent", color: t.textMuted, border: `1px solid ${t.border}`,
          padding: "8px 16px", fontSize: 10, fontFamily: t.fontBody,
          letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
        }}>Refresh</button>
      </div>
      <div style={{ background: t.surface, border: `1px solid ${t.border}` }}>
        {loading ? <Loader /> : <DataTable data={data || []} columns={columns} />}
      </div>
    </div>
  );
}

// ─── Config Tab ───────────────────────────────────────────────────────────────
function ConfigTab({ token }) {
  const { data, loading, refetch } = useAPI("/api/config", token);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saved, setSaved] = useState(null);
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  const saveConfig = async (key) => {
    const res = await fetch(`${API_BASE}/api/config/update`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ key, value: editVal }),
    });
    if (res.ok) {
      setSaved(key);
      setEditing(null);
      refetch();
      setTimeout(() => setSaved(null), 2000);
    }
  };

  const toggleGroup = (group) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const CONFIG_GROUPS = {
    "Capital & Deployment": {
      icon: "◈", color: "#7B5EA7",
      keys: ["PAPER_TRADING", "PAPER_TRADING_CAPITAL", "BULL_CAPITAL_DEPLOYED", "SIDEWAYS_CAPITAL_DEPLOYED", "BEAR_CAPITAL_DEPLOYED", "MIN_BUYING_POWER_BUFFER", "CLIENT_MODE"],
    },
    "Position Sizing": {
      icon: "◉", color: "#4A9B6F",
      keys: ["MAX_POSITION_SIZE", "KELLY_FRACTION", "KELLY_MAX", "KELLY_LOOKBACK", "TARGET_VOLATILITY", "LARGE_CAP_ALLOCATION", "MID_CAP_ALLOCATION", "SMALL_CAP_ALLOCATION"],
    },
    "Risk Management": {
      icon: "⬡", color: "#C0392B",
      keys: ["LONG_STOP_LOSS", "SHORT_STOP_LOSS", "TRAILING_STOP_LOSS", "PROFIT_TAKE_PARTIAL", "PROFIT_TAKE_LARGE", "MAX_SECTOR_EXPOSURE", "MAX_PORTFOLIO_BETA", "MAX_VOLATILITY", "HIGH_VOLATILITY_THRESHOLD", "LOW_VOLATILITY_THRESHOLD", "CVAR_CONFIDENCE", "CVAR_LOOKBACK"],
    },
    "Conviction & Signals": {
      icon: "◆", color: "#E8A838",
      keys: ["STRONG_BUY_MULTIPLIER", "STRONG_SHORT_MULTIPLIER", "SIGNAL_COUNT_4_BOOST", "SIGNAL_COUNT_3_BOOST", "SIGNAL_COUNT_1_PENALTY", "HIGH_CONVICTION_THRESHOLD", "PAPER_STRONG_BUY_THRESHOLD", "PAPER_BUY_THRESHOLD", "LIVE_STRONG_BUY_THRESHOLD", "LIVE_BUY_THRESHOLD"],
    },
    "Signal Weights": {
      icon: "◎", color: "#A78BDB",
      keys: ["MANUAL_WEIGHT_OVERRIDE", "WEIGHT_TECHNICAL", "WEIGHT_SENTIMENT", "WEIGHT_GRAPH", "WEIGHT_STAT_ARB", "WEIGHT_REGIME", "WEIGHT_TOPOLOGY", "MOMENTUM_BULL_WEIGHT", "MOMENTUM_SIDEWAYS_WEIGHT", "MOMENTUM_BEAR_WEIGHT"],
    },
    "Ensemble": {
      icon: "▣", color: "#5BA4CF",
      keys: ["ENSEMBLE_LOOKBACK_DAYS", "ENSEMBLE_LEARNING_RATE", "MIN_JUDGE_WEIGHT", "MAX_JUDGE_WEIGHT", "COUNTERFACTUAL_WEIGHT"],
    },
    "Exit Rules": {
      icon: "◐", color: "#E8A838",
      keys: ["EARNINGS_PROFIT_REDUCE", "EARNINGS_SMALL_PROFIT_REDUCE", "SQUEEZE_SHORT_COVER_THRESHOLD", "SQUEEZE_SHORT_PCT_HIGH", "MACRO_EXIT_THRESHOLD", "TWAP_DURATION_MINUTES", "ORDER_DELAY_SECONDS"],
    },
    "Short Selling": {
      icon: "◑", color: "#C0392B",
      keys: ["ALLOW_SHORTING", "MAX_SHORT_ALLOCATION", "MIN_SHORT_SIGNALS_REQUIRED", "SHORT_CONVICTION_THRESHOLD", "MAX_SHORT_POSITION_SIZE"],
    },
    "Regime & Topology": {
      icon: "◒", color: "#4A9B6F",
      keys: ["SIDEWAYS_MULTIPLIER", "BEAR_MULTIPLIER", "BULL_MULTIPLIER", "TOPOLOGY_HIGH_MULTIPLIER", "TOPOLOGY_MEDIUM_MULTIPLIER"],
    },
    "Earnings": {
      icon: "▧", color: "#E8A838",
      keys: ["USE_EARNINGS_FILTER", "EARNINGS_BLACKOUT_DAYS", "EARNINGS_POSITION_REDUCTION"],
    },
    "Macro & Cross Asset": {
      icon: "◔", color: "#5BA4CF",
      keys: ["USE_MACRO_FACTORS", "USE_CROSS_ASSET", "YIELD_CURVE_INVERSION_THRESHOLD", "CREDIT_SPREAD_HIGH_THRESHOLD", "DXY_STRONG_THRESHOLD"],
    },
    "Short Interest": {
      icon: "◕", color: "#C0392B",
      keys: ["USE_SHORT_INTEREST", "HIGH_SHORT_INTEREST"],
    },
    "Options Flow": {
      icon: "◓", color: "#A78BDB",
      keys: ["USE_OPTIONS_FLOW"],
    },
    "Insider & Congressional": {
      icon: "▤", color: "#A78BDB",
      keys: ["USE_INSIDER_TRACKING", "USE_CONGRESSIONAL_TRADING"],
    },
    "Graph Signal": {
      icon: "○", color: "#5BA4CF",
      keys: ["GSP_ALPHA", "GSP_J", "GSP_THRESHOLD", "GSP_SIGNAL_WEIGHT"],
    },
    "Advanced Layers": {
      icon: "▨", color: "#6B6B88",
      keys: ["USE_KALMAN_FILTER", "USE_HRP", "USE_TRANSFORMER", "USE_CVAR", "USE_VWAP", "USE_FOURIER", "USE_REINFORCEMENT_LEARNING", "USE_JOB_POSTINGS", "USE_KELLY_CRITERION", "FOURIER_HARMONICS"],
    },
    "Tax Settings": {
      icon: "▩", color: "#4A9B6F",
      keys: ["ORDINARY_INCOME_RATE", "SHORT_TERM_CAPITAL_GAINS", "LONG_TERM_CAPITAL_GAINS", "STATE_TAX_RATE", "TAX_AWARE_TRADING", "MIN_SHORT_TERM_PREMIUM"],
    },
    "Universe & Data": {
      icon: "·", color: "#6B6B88",
      keys: ["MIN_MARKET_CAP", "MIN_AVG_VOLUME", "LARGE_CAP_MIN", "MID_CAP_MIN", "SMALL_CAP_MIN", "RISK_FREE_RATE", "MIN_ANNUAL_RETURN_TARGET", "SAVE_SIGNAL_HISTORY", "DASHBOARD_PORT"],
    },
    "IBKR Connection": {
      icon: "▪", color: "#6B6B88",
      keys: ["IBKR_HOST", "IBKR_PORT", "IBKR_CLIENT_ID", "IBKR_ACCOUNT"],
    },
    "Other": {
      icon: "·", color: "#6B6B88",
      keys: [],
    },
  };

  const getGroup = (key) => {
    for (const [group, cfg] of Object.entries(CONFIG_GROUPS)) {
      if (cfg.keys.includes(key)) return group;
    }
    return "Other";
  };

  const allEntries = Object.entries(data || {}).filter(([k]) =>
    k.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  allEntries.forEach(([k, v]) => {
    const g = getGroup(k);
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push([k, v]);
  });

  const visibleGroups = Object.entries(grouped).filter(([, rows]) => rows.length > 0);

  const ConfigRow = ({ keyName, val }) => (
    <div
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = t.surfaceHigh}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <span style={{ fontFamily: t.fontMono, color: saved === keyName ? t.positive : t.textMuted, fontSize: 11, flex: 1 }}>
        {keyName}
      </span>
      {editing === keyName ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onKeyDown={e => e.key === "Enter" && saveConfig(keyName)}
            autoFocus
            style={{ background: t.bgDeep, border: `1px solid ${t.purple}`, color: t.textPrimary, padding: "6px 10px", fontSize: 12, fontFamily: t.fontMono, width: 180, outline: "none" }}
          />
          <button onClick={() => saveConfig(keyName)} style={{ background: t.purple, color: t.textPrimary, border: "none", padding: "6px 14px", fontSize: 10, fontFamily: t.fontBody, fontWeight: 500, cursor: "pointer" }}>Save</button>
          <button onClick={() => setEditing(null)} style={{ background: "transparent", color: t.textMuted, border: `1px solid ${t.border}`, padding: "6px 12px", fontSize: 10, cursor: "pointer", fontFamily: t.fontBody }}>✕</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <span style={{ fontFamily: t.fontMono, color: saved === keyName ? t.positive : t.textSilver, fontSize: 12 }}>{String(val)}</span>
          <button
            onClick={() => { setEditing(keyName); setEditVal(val); }}
            style={{ background: "transparent", color: t.textMuted, border: `1px solid ${t.border}`, padding: "4px 12px", fontSize: 10, cursor: "pointer", fontFamily: t.fontBody }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = t.purple; e.currentTarget.style.color = t.purpleLight; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textMuted; }}
          >Edit</button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <h2 style={{ fontFamily: t.fontDisplay, fontSize: 28, fontWeight: 400, color: t.textPrimary, letterSpacing: 1, marginBottom: 8 }}>
        Config Editor
      </h2>
      <p style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12, marginBottom: 24 }}>
        Live edit bot configuration — changes take effect on next pipeline run
      </p>

      <input
        placeholder="Search config keys..."
        value={search}
        onChange={e => {
          setSearch(e.target.value);
          if (e.target.value) {
            const allOpen = {};
            visibleGroups.forEach(([g]) => allOpen[g] = true);
            setOpenGroups(allOpen);
          }
        }}
        style={{ width: 280, background: t.surface, border: `1px solid ${t.border}`, borderBottom: `1px solid ${t.purple}`, color: t.textPrimary, padding: "10px 14px", fontSize: 12, fontFamily: t.fontMono, outline: "none", marginBottom: 24 }}
      />

      {loading ? <Loader /> : visibleGroups.map(([group, rows]) => {
        const cfg = CONFIG_GROUPS[group] || { icon: "·", color: t.textMuted };
        const isOpen = !!openGroups[group];
        return (
          <div key={group} style={{ marginBottom: 6 }}>
            <div
              onClick={() => toggleGroup(group)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "13px 20px",
                background: isOpen ? t.surfaceHigh : t.surface,
                border: `1px solid ${isOpen ? t.purpleDim : t.border}`,
                borderLeft: `3px solid ${cfg.color}`,
                cursor: "pointer", userSelect: "none",
              }}
              onMouseEnter={e => e.currentTarget.style.background = t.surfaceHigh}
              onMouseLeave={e => e.currentTarget.style.background = isOpen ? t.surfaceHigh : t.surface}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: cfg.color, fontSize: 14 }}>{cfg.icon}</span>
                <span style={{ fontFamily: t.fontBody, fontSize: 11, fontWeight: 600, color: t.textPrimary, letterSpacing: 2, textTransform: "uppercase" }}>
                  {group}
                </span>
                <span style={{ fontFamily: t.fontMono, fontSize: 10, color: t.textMuted, background: t.bgDeep, padding: "2px 8px", borderRadius: 10 }}>
                  {rows.length}
                </span>
              </div>
              <span style={{ color: t.textMuted, fontSize: 12, display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                ▾
              </span>
            </div>
            {isOpen && (
              <div style={{ background: t.bgDeep, border: `1px solid ${t.border}`, borderTop: "none" }}>
                {rows.map(([k, v]) => <ConfigRow key={k} keyName={k} val={v} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Admin Dashboard ─────────────────────────────────────────────────────
export default function AdminDashboard({ user, onLogout }) {
  const token = user.access_token;
  const [activeTab, setActiveTab] = useState("portfolio");
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem(`theme_${user.username}`) || "dark"
  );
  t = themeMode === "dark" ? darkTheme : lightTheme;

  const toggleTheme = () => {
    const next = themeMode === "dark" ? "light" : "dark";
    setThemeMode(next);
    localStorage.setItem(`theme_${user.username}`, next);
  };

  const renderTab = () => {
  switch (activeTab) {
    case "portfolio":   return <PortfolioTab   token={token} />;
    case "performance": return <PerformanceTab token={token} />;
    case "signals":     return <SignalsTab     token={token} />;
    case "market":      return <MarketTab      token={token} />;
    case "scanner":     return <ScannerTab     token={token} />;
    case "clients":     return <ClientsTab     token={token} />;
    case "pipeline":    return <PipelineTab    token={token} />;
    case "config":      return <ConfigTab      token={token} />;
    default:            return <PortfolioTab   token={token} />;
  }
};

  return (
    <>
      <FontLink />
      <div style={{ minHeight: "100vh", background: t.bg, color: t.textPrimary, fontFamily: t.fontBody }}>

        {/* Top bar */}
        <div style={{
          background: t.surface, borderBottom: `1px solid ${t.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 32px", height: 56, position: "sticky", top: 0, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <img 
              src="/logo.png" 
              alt="Wayne Enterprises" 
              style={{ height: 36, width: "auto", flexShrink: 0 }}
            />
            <span style={{ fontFamily: t.fontDisplay, color: t.textPrimary, fontSize: 18, letterSpacing: 1 }}>
              Wayne Enterprises
            </span>
            <span style={{ color: t.border, fontSize: 20 }}>|</span>
            <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 10, letterSpacing: 4, textTransform: "uppercase" }}>
              Admin
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <span style={{ fontFamily: t.fontBody, color: t.textMuted, fontSize: 12 }}>{user.name}</span>
            <button onClick={toggleTheme} style={{
              background: "transparent",
              border: `1px solid ${t.borderGold}`,
              padding: "6px 14px",
              fontSize: 10,
              fontFamily: t.fontBody,
              fontWeight: t.weightBody,
              letterSpacing: 2,
              textTransform: "uppercase",
              cursor: "pointer",
              color: t.gold,
            }}>
              {themeMode === "dark" ? "☀ Light" : "☾ Dark"}
            </button>
            <button onClick={onLogout} style={{
              background: "transparent", color: t.textMuted,
              border: `1px solid ${t.border}`, padding: "6px 14px",
              fontSize: 10, fontFamily: t.fontBody, letterSpacing: 2,
              textTransform: "uppercase", cursor: "pointer",
            }}>Logout</button>
          </div>
        </div>

        <div style={{ display: "flex", height: "calc(100vh - 56px)" }}>
          {/* Sidebar */}
          <div style={{
            width: 190,
            background: t.navBg,
            borderRight: `1px solid ${t.navBorder}`,
            padding: "28px 0",
            flexShrink: 0,
            backgroundImage: `radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.05) 0%, transparent 70%)`,
          }}>
            {NAV.map(item => (
              <button key={item.key} onClick={() => setActiveTab(item.key)} style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "13px 24px",
                color: activeTab === item.key ? t.navActive : t.navText,
                fontFamily: t.fontBody,
                fontSize: 11,
                fontWeight: activeTab === item.key ? t.weightSub : t.weightBody,
                letterSpacing: 2,
                textTransform: "uppercase",
                border: "none",
                borderLeft: `2px solid ${activeTab === item.key ? t.navActiveBorder : "transparent"}`,
                borderBottom: activeTab === item.key ? `1px solid ${t.tabUnderline}` : "1px solid transparent",
                background: activeTab === item.key ? t.navActiveBg : "transparent",
                cursor: "pointer",
                transition: "all 0.15s",
              }}>
                {item.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: "36px 40px", overflowY: "auto" }}>
            {renderTab()}
          </div>
        </div>
      </div>
    </>
  );
}